import { AppContext } from "App";

import { ROLELESS_ROLE_ID } from "Global/Base/Rights/constants";
import { ArrayUtils } from "Global/Base/Utils/ArrayUtils";
import { ObjectUtils } from "Global/Base/Utils/ObjectUtils";
import { InvalidTypeCasting } from "OW.Remoting/Basic/OWExceptions";
import { OWObjectList } from "OW.Remoting/Remoting/DataTypes/OWObjectList";
import { IDataEventsManager } from "OW.Remoting/Remoting/IDataEventsManager";
import { RowPermissions } from "OW.Remoting/Remoting/RowPermissions";
import { SerializeFuncs } from "OW.Remoting/Remoting/Serializers";
import { ErrorHandler } from "UI/Errors";
import { MEOWObject } from "UI/UI.Controls/DataSource/MultiEdit/MEOWObject";
import { ListDataGrid } from "UI/UI.Controls/ListDataGrid";
import { ControlRightTypesGroup, RightGroups } from "UI/UI/Rights/RightGroups";
import { DataConverter } from "../../../../../Global/Base/Converters/DataConverter";
import { DataInterfaceComparer } from "../../../../../Global/Base/DataSources/DataInterfaceComparer";
import {
    AdditionalOrdering,
    ConditionParam,
    IDataSourceLoadInfo,
    IEditableDataSource,
    IOrderBy,
    IPermissionData,
    OrderType,
} from "../../../../../Global/Base/DataSources/Interfaces";
import { DIContainer } from "../../../../../Global/Base/DIContainer";
import { Event as GlobalEvent, EventAwaiter, StatefulEvent } from "../../../../../Global/Base/Events";
import { InvalidOperationException } from "../../../../../Global/Base/Exceptions";
import { CancelError, CancellationToken, CancellationTokenSource } from "../../../../../Global/Base/Promises/CancellationTokenSource";
import { Deferred, DeferredStateful, PromiseUtils } from "../../../../../Global/Base/Promises/PromiseUtils";
import { StringUtils } from "../../../../../Global/Base/Utils/StringUtils";
import { ValidationErrorType } from "../../../../../OW.Remoting/Basic/EOWExceptionsInterfaces";
import { AttrValueReader } from "../../../../../OW.Remoting/Remoting/AttrValueReader";
import { BasicDataType, IOWClassDescription } from "../../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { ModelDescriptionsManager } from "../../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { IOWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/IOWObject";
import { IValidationDataObject } from "../../../../../OW.Remoting/Remoting/DataTypes/IValidationDataObject";
import { NullOWObject, OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { InstanceManager } from "../../../../../OW.Remoting/Remoting/InstanceManager";
import { IWFOpenFormEventArgs, IWFOpenFormParams } from "../../../../../OW.Remoting/Remoting/IWorkflowManager";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { IImplicitFormInfo, IListFrameColumn } from "../../../../../OW.Remoting/Remoting/OWManagerInterfaces";
import { PasswordManager } from "../../../../../OW.Remoting/Remoting/PasswordManager";
import { Control as BaseControl } from "../../../../UI.BaseControls/Control";
import { IBussyAwaitable, IValidatable, IValidationResult } from "../../../../UI.BaseControls/ControlInterfaces";
import { EventHelper } from "../../../../UI.BaseControls/Helpers/EventHelper";
import { IBooleanDetailValueEvent } from "../../../../UI.BaseControls/IEvent";
import { ActionType, IAction } from "../../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../../UI.Controls/Actions/MenuType";
import { CompactDataGrid, ICompactDataGridAttrInfo } from "../../../../UI.Controls/CompactDatagrid";
import { DataGrid } from "../../../../UI.Controls/DataGrid";
import { Column } from "../../../../UI.Controls/DataGridSubcontrols/Columns/Column";
import { ColumnFactory, IColumnDefinition, IColumnParameters } from "../../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { CompactDataRow } from "../../../../UI.Controls/DataGridSubcontrols/Components/CompactDataRow";
import { DataRow } from "../../../../UI.Controls/DataGridSubcontrols/Components/DataRow";
import { IDataRowElement, IEditableDataRowElement, IEditableListDataRowElement } from "../../../../UI.Controls/DataGridSubcontrols/Components/IDataRow";
import { TableCell } from "../../../../UI.Controls/DataGridSubcontrols/Components/TableCell";
import { DBDataSource } from "../../../../UI.Controls/DataSource/DBDataSource";
import { EditableDBDataSource } from "../../../../UI.Controls/DataSource/EditableDBDataSource";
import { IObjectDataSource } from "../../../../UI.Controls/DataSource/IObjectDataSource";
import { DropDownMenu } from "../../../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuItem } from "../../../../UI.Controls/DropDownMenu/DropDownMenuItem";
import { SeparatorMenuItem } from "../../../../UI.Controls/DropDownMenu/SeparatorItem";
import { EditableDataGrid, IPermissionChanges } from "../../../../UI.Controls/EditableDatagrid";
import { IDataGrid, IEditableListDataGrid, IEmptyMessageDataGrid } from "../../../../UI.Controls/IDataGrid";
import {
    ICellValueChangedPrewAwaitableEvent,
    IColumnOrderingChangedEvent,
    IColumnPositionChangedEvent,
    IDataGridSelectionChangedEvent,
} from "../../../../UI.Controls/IEvent";
import { convertFormOpenTypeToPurposeType } from "../../../Utilities/SharedUIMethods";
import { AutoRefresher } from "../../AutoRefresher";
import { Control } from "../../Controls/Control";
import { FormControlWithLoggerParams, IAttributeControl, IGenericControl, IInfoProvidingControl } from "../../Controls/ControlInterfaces";
import { IFRMParams } from "../../Controls/FormParameters";
import { LOVEditMaster } from "../../Controls/LOVEditMaster";
import { CONST_STATUS_VIEW_ENABLED } from "../../Controls/WorkflowEditor";
import { ColumnInfoBuilder } from "../../DataBuilders/ColumnInfoBuilder";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { DownloadManager } from "../../DownloadManager";
import { FormManager } from "../../FormManager/IFormManager";
import { FormUICustomProperty } from "../../FormUI/FormUI";
import { ISystemOperationRights, SystemOperation } from "../../Helpers/SystemOperationRights";
import { FrameColumnInfoAttributes, IFrameColumnInfo, IFrameProfileInfo } from "../../IFrameColumnInfo";
import { MessageButtonType, MessageWindow } from "../../MessageWindow";
import { MessageButtonResult, UIManager } from "../../UIManager";
import { WindowState } from "../../UIManagerInterfaces";
import { OpenModeType } from "../Actions/DetailFormAction";
import { StandardAction } from "../Actions/StandardAction";
import { IControlInfoObject } from "../ControlInfo";
import { LOVEdit } from "../Controls/LOVEdit";
import { DropDownMenuFilterItem } from "../Filters/DropDownMenuItems/DropDownMenuFilterItem";
import { FilterManager } from "../Filters/FilterManager";
import { IFilterManager } from "../Filters/IFilterManager";
import { Form } from "../Form";
import { IActualObjectChangedEventArgs } from "../Frames";
import { IExtendedLifeCycle } from "../IExtendedLifeCycle";
import { IForm } from "../IForm";
import { IListFrame } from "../IFrame";
import { ModalWindowType } from "../ModalWindow";
import { SeparateDetailFrame } from "../SeparateDetailFrame";
import { Pages } from "../TabPages";
import { IValidationData } from "../ValidationManager";
import { BaseFrame } from "./BaseFrame";
import { FileColumn } from "./ColumnTemplates/FileColumn";
import { IRequiresMasterObject } from "./ColumnTemplates/IRequiresMasterObject";
import { IMasterObjectRequest, LovColumn } from "./ColumnTemplates/LOVColumn";
import {
    EditStyleType,
    IFrameWithFiltering,
    IMasterableFrame,
    IMasterableFrameWithDataGrid,
    IMultiselectComponent,
    ISpecificallyLoadableFrame,
} from "./FrameInterfaces";
import { FrameSharedMethods, IExportData } from "./FrameSharedMethods";
import { IEditableFrame } from "./IEditableFrame";

export abstract class GenericListFrame extends BaseFrame implements IListFrame, IMasterableFrameWithDataGrid, IEditableFrame, ISpecificallyLoadableFrame, IExtendedLifeCycle, IInfoProvidingControl, IFrameWithFiltering, IBussyAwaitable, IMultiselectComponent {
    //#region Events
    /**
     * Událost vyvolávaná při změně stavu editace (aktivní/neaktivní)
     */
    public readonly editModeChanged: GlobalEvent<GenericListFrame, boolean> = new GlobalEvent<GenericListFrame, boolean>();
    protected readonly _orderByInitiated: GlobalEvent<GenericListFrame, IOrderBy[]> = new GlobalEvent<GenericListFrame, IOrderBy[]>();
    public override readonly dataGridInitialized: StatefulEvent<IMasterableFrameWithDataGrid, IEmptyMessageDataGrid<IDataRowElement>> = new StatefulEvent<IMasterableFrameWithDataGrid, IEmptyMessageDataGrid<IDataRowElement>>();
    public readonly dataGridCreated: GlobalEvent<IMasterableFrameWithDataGrid, IEmptyMessageDataGrid<IDataRowElement>> = new GlobalEvent<IMasterableFrameWithDataGrid, IEmptyMessageDataGrid<IDataRowElement>>();
    public readonly dataSaved: GlobalEvent<GenericListFrame, void> = new GlobalEvent<GenericListFrame, void>();
    public readonly columnsUpdated: GlobalEvent<GenericListFrame, { columns: Column[]; profileInfo: IFrameProfileInfo }> = new GlobalEvent<GenericListFrame, { columns: Column[]; profileInfo: IFrameProfileInfo }>();

    /**
     * Událost o tom, že byl nastaven (neprázdný) actualObject
     */
    public readonly actualObjectSet: GlobalEvent<GenericListFrame, OWObject> = new GlobalEvent<GenericListFrame, OWObject>();
    //#endregion Events

    //#region Fields
    /**
     * Zakáže možnost řazení
     */
    public disableSort: boolean = false;
    /**
     * Otevřené modální MasterableFramy, hodí se pro práci v editačním kontextu
     */
    public openedModalMasterableFrames: readonly IMasterableFrame[] = [];

    public onEditMethod: string;
    public onCancelMethod: string;

    /**
     * Zjištění, zda by se měl posílat dotaz na vyhodnocení InsertRight
     */
    public get hasInsertControl(): boolean {
        return !this.hideToolBar &&
            ((this.isMainFrame && this.detail && (!this.detail?.hideButtons?.insert || !this.detail?.hideButtons?.copy)) ||
                ((this.isEditable || !this.isMainFrame || this.detailForm) && (!this.hideButtons?.insert || !this.hideButtons?.copy)));
    }

    private static _counter = 0;

    public get staticCounter(): number {
        return GenericListFrame._counter++;
    }

    public override masterAttr: string;

    private __classDescription: IOWClassDescription | null = null;

    protected _openDetailInNewWindow: boolean = false;

    protected _filterManager: FilterManager;
    protected _headerDropDown: DropDownMenu;
    protected _headerDropDownFilterItem: DropDownMenuFilterItem;
    protected _headerDropDownHc: Column;
    protected _hideFilterRow: boolean = false;
    protected _color: string;
    protected _frozenColumnCount: number = 0;
    protected _autoRefreshSlave: boolean = true;
    protected _decryptItemAction: StandardAction;
    protected _addAttrsListMethod: string;
    protected _showAuditData: boolean = true;

    private _dataLoadingDefferred: Deferred<boolean>;
    private _dataLoadingEndedDefferred: Deferred<boolean>;
    private _firstLoadFinished = false;

    /**
     * Příznak, zda byl ukončen querymode
    */
    protected _queryModeFinished: boolean = false;
    public orderBy: IOrderBy[];
    /* Třídění podle definice formuláře */
    private _definitionOrderBy: IOrderBy[];
    /**Podminka vytazena z definice seznamu*/
    private _defaultFilterId: string | null;
    public override condition: string;
    /** Zda má kontrola a zobrazení YNC proběhnout jen nad aktuálním framem */
    public checkAndSaveActualFrameOnly = false;

    private _conditionUpdated: boolean = false;

    private _editStyle: EditStyleType = EditStyleType.esSingle;

    protected _openMode: OpenModeType;

    public dataGrid: IEditableListDataGrid<IEditableListDataRowElement>;

    public compactDataGrid: CompactDataGrid<CompactDataRow>;

    private _currentDataGrid: IDataGrid<IDataRowElement>;

    public dataSource: IEditableDataSource & IObjectDataSource;

    public override fontColorAttr: string;
    public override fontColorFlags: string;
    protected _columnLoadingFinished: DeferredStateful<boolean> = PromiseUtils.deferStateful<boolean>();
    protected _isDataGridInitialized: boolean = false;

    private _autoRefreshInterval: number;
    private _dynamicColumns: IListFrameColumn[];
    private _dataLoadingCTS: CancellationTokenSource;
    protected _lastSelectedRecordId = "-1";

    private __columnInfoBuilder?: ColumnInfoBuilder;
    public isInitExecuteFilter: boolean;
    //#endregion Fields

    //#region Properties
    protected _formObjectName: string;
    public get formObjectName(): string {
        return this._formObjectName;
    }

    public get filterManager(): IFilterManager {
        return this._filterManager;
    }

    public override get isMultiSelecting(): boolean {
        return this.supportsMultiEditing && (this.currentDataGrid?.selectedRows.length > 1 || this.currentDataGrid?.isSelectedAll || false);
    }

    public override get interactive(): boolean {
        return super.interactive;
    }

    public override set interactive(value: boolean) {
        super.interactive = value;

        if (this.dataGrid)
            this.dataGrid.interactive = this.interactive;

        if (this.compactDataGrid)
            this.compactDataGrid.interactive = this.interactive;
    }

    public get actualObjectHasWF(): boolean {
        return this._actualObjectHasWF;
    }

    /**
     * constanta prazdneho ID zaznamu
     */
    protected static readonly _NO_ID: string = "-1";

    //#endregion Fields

    /** Objekt, nad kterým se vyhodnocují dynamické sloupce */
    protected get _dynamicColumnsObject(): OWObject | null {
        return this.masterObject || (this.isMainFrame && this.ownerForm.masterObject);
    }

    public override get actualObject(): OWObject {
        return super.actualObject;
    }

    public override set actualObject(object: OWObject) {
        this._setActualObject(object);
    }

    /**
     * Pokud je selectedRecordChangeDeferred resolvnutý, vytvoří nový.
     * @returns Vrací true, pokud byl vytvořen nový deferred. Jinak false.
     */
    protected _tryRenewSelectedRecordChangeDeferred(): boolean {
        if (this._selectedRecordChangeDeferred.isResolved) {
            this._selectedRecordChangeDeferred = PromiseUtils.deferStateful<boolean>();

            return true;
        }

        return false;
    }

    protected _onObjectDataRefreshed(obj: OWObject): void {
        if (!this._autoRefreshSlave || obj !== this.actualObject)
            return;

        this.triggerActualObjectChange(obj, obj); // vyvolá refresh podřízených framů

        this._triggerRightsChecking(); // vyvola aktualizaci prav
    }

    public override get profileInfo(): IFrameProfileInfo {
        return this._profileInfo;
    }

    public override set profileInfo(info: IFrameProfileInfo) {
        // u dynamických sloupců potlačujeme možnost ukládání profilu
        if (this._addAttrsListMethod) {
            return;
        }

        // je potreba pockat az po nacteni dg
        this.dataGridInitialized.add(async () => {
            await this._setProfileInfo(info);
        });
    }

    protected async _setProfileInfo(info: IFrameProfileInfo): Promise<void> {
        // pokud polozka neni ještě viditelna je potreba nastavit profil az po jejim zviditelneni jinak nejsou dostupne sloupce a nelze tedy menit nastaveni
        if (!this.canValueLoad) {
            const canValueLoadFnc = async (_ex: Control, val: boolean) => {
                if (val) {
                    await this._waitForColumnLoad();
                    this.profileInfo = info;
                    this.canValueLoadChanged.remove(canValueLoadFnc);
                }
            };
            this.canValueLoadChanged.add(canValueLoadFnc);
            return;
        }

        if (JSON.stringify(this.profileInfo) !== JSON.stringify(info)) // kontrola instance profilu, pokud je stejny, neni potreba mazat
            await this._clearProfileInfo(true);

        const oldDataSourceAttributes = this.dataSource.getAttrs();

        const ordArr: Array<{ column: Column; profile: IFrameColumnInfo }> = [];
        const keys = Object.keys(info.columns);

        if (info.order && info.order.length > 0) {
            this.dataGrid.setOrderBy(info.order, false);
            this.orderBy = info.order;
        } else {
            if (this.dataGrid.setDefaultOrderBy(this._definitionOrderBy)) // pokud bylo třídění změněno
                this.orderBy = this.dataGrid.orderBy;
        }

        for (const key of keys) {
            const item = info.columns[key];
            const column = this.dataGrid.columns.find((col) => col.attrName == item.attrName);
            ordArr.push({ column: column, profile: item });
        }

        // Pole sloupců gridu setřízené v pořadí, které odpovídá pořadí uloženému v profilu
        const sortedColumnsArr = this._sortColumnsByOrder(ordArr);

        // Sloupce gridu se přesunou podle jejich pořadí v setřízeném poli
        for (const [index, column] of sortedColumnsArr.entries()) {
            this.dataGrid.setColumnOrder(column, index + 1);
        }

        // Sloupcům gridu se nastaví viditelnost a šířka
        for (const item of ordArr) {
            if (!item.profile || !item.column)
                continue;
            if (item.profile.visible !== undefined) {
                item.column.visible = item.profile.visible;
                item.column.headerCell.visibility = item.profile.visible;
            }
            if (item.profile.width !== undefined)
                this.dataGrid.setColumnWidth(item.column, item.profile.width);
        }

        const newDataSourceAttributesList = this.dataGrid.columns.filter(item => item.visible);
        let fullLengthAttributes = newDataSourceAttributesList.filter(column => column.trimValue === false).map(column => column.attrName);

        let newDataSourceAttributes: string[] = [];
        for (const nattr of newDataSourceAttributesList) {
            if (!nattr.withoutAttribute) {
                newDataSourceAttributes = newDataSourceAttributes.concat(nattr.dataSourceAttrNames);
            }
            if (nattr.trimValue === false) {
                fullLengthAttributes = fullLengthAttributes.concat(nattr.dataSourceAttrNames);
            }
        }

        // If the fontColorAttr is defined, we always need it in the array of attributes.
        if (this.fontColorAttr && !newDataSourceAttributes.includes(this.fontColorAttr)) {
            newDataSourceAttributes.push(this.fontColorAttr);
        }

        this.dataSource.setAttrs(newDataSourceAttributes, fullLengthAttributes);
        await this._setShowSubRecords(info.showSubRecords ?? false);

        this._profileInfo = info;

        this.selectedProfileChanged.trigger(this, info);

        await this._profileChangeRefresh(newDataSourceAttributes.some(attr => oldDataSourceAttributes.indexOf(attr) === -1));
    }

    private async _setShowSubRecords(value: boolean): Promise<void> {
        if (value === this.showSubRecords || !await this._showSubRecordsSupported())
            return;

        this.showSubRecords = value;
        if (this._showSubRecordsAction)
            this._showSubRecordsAction.checked = value;

        await this.updateCondition(this.dataSource);
    }

    /**
     * Podpora multieditace - defaultne je esSingle (editace jen jednoho radku bez ulozeni).
     * esMulti je vychozi hodnota u edit. seznamu a u ObjectListEditu
     */
    public get editStyle(): EditStyleType {
        return this._editStyle;
    }

    public set editStyle(value: EditStyleType) {
        this._editStyle = value;
    }

    public override get isMultiEditing(): boolean {
        return this._isMultiEditing;
    }
    public override set isMultiEditing(value: boolean) {
        this._isMultiEditing = value;
        this.dataGrid.isMultiEditAllowed = value || this.editStyle == EditStyleType.esMulti;
    }

    public override get masterFrame(): IMasterableFrame {
        return this._masterFrame;
    }

    public override set masterFrame(masterFrame: IMasterableFrame) {
        const subListRefreshKey = "genericListFrameSubListRefresh";
        this._events.removeByKey(subListRefreshKey);

        this._masterFrame = masterFrame;

        if (this.masterFrame == null)
            return;

        if (this.masterFrame instanceof IMasterableFrame) {
            this._events.addWithKey(subListRefreshKey, this.masterFrame.actualObjectSaved, async () => {
                await this._requestDynamicColumnsUpdate(); // Musí být spuštěno před případným refreshem dat níže

                if (this.canValueLoad) {
                    await this.updateCondition(this.dataSource);

                    if (this._isDisposed)
                        return;

                    this.refreshData(false);
                }
            });
        }
    }

    /**
     * Informace o tom, zda je seznam editovatelny nebo neni
     */
    public isEditable: boolean;

    /**
     * Vyčistí nastavení profilu a vrátí seznam do původního stavu
     */
    public override clearProfileInfo(): void {
        if (this._addAttrsListMethod) {
            return;
        }

        if (this._isDataGridInitialized) {
            this._clearProfileInfo();
            this.selectedProfileChanged.trigger(this, this.profileInfo);
        } else {
            this.dataGridInitialized.add(() => {
                this._clearProfileInfo();
            }, true);
        }
    }

    private async _clearProfileInfo(withoutReload: boolean = false): Promise<void> {
        if (this._addAttrsListMethod || !this.currentDataGrid || !this.currentDataGrid.canValueLoad)
            return;

        // Nepoužívat zde setter předka ani této třídy, způsobuje vícenásobné volání selectedProfileChanged, protože je tato metoda volána právě i ze setteru
        this._profileInfo = { columns: {}, order: this._definitionOrderBy };
        const attrs = Array.from(this.element.querySelectorAll("[data-behaviour='column']")) as HTMLElement[];
        await this._setShowSubRecords(false);

        for (const [index, attr] of attrs.entries()) {
            const attrName = attr.getAttribute("data-attrname");
            const width = DataConverter.toNumber(attr.getAttribute("data-width"));
            const visibility = DataConverter.toBoolean(attr.getAttribute("data-visible"), true);
            const column = this.dataGrid.columns.find((item) => StringUtils.compareCaseInsensitive(item.attrName, attrName));

            this.dataGrid.setColumnOrder(column, index + 1); // nastaví se podle pozice sloupce na seznamu, počítá se od 1 kvůli profilům
            this.dataGrid.setColumnWidth(column, width);
            column.visible = visibility;
        }

        if (!withoutReload) {
            const orderByChanged = this.dataGrid.setDefaultOrderBy(this._definitionOrderBy);
            if (orderByChanged || this._conditionUpdated) { // pokud bylo třídění změněno
                this.orderBy = this.dataGrid.orderBy;
                this.dataSource.loadNewData(undefined, this._cancelPrevAndCreateDataLoadingCTS().token);
            } else {
                await this.dataGrid.reRenderData();
            }
        }
    }

    /** Vrací informaci o tom, zda je aktivní režim QueryMode */
    protected _isInQueryMode(): boolean {
        if (this._queryModeFinished)
            return false;

        return this.currentDataGrid instanceof IEmptyMessageDataGrid && this.currentDataGrid.isInQueryMode();
    }

    /**
     * Vytvoří pole o velikosti všech sloupců gridu, kde budou všechny sloupce gridu seřazené tak, aby respektovaly 'order' sloupců v profilech
     * Nejdříve se do pole vloží sloupce, jejichž 'order' je uložený v profilech a to právě na pozice, ve kterých se mají nacházet po přesunutí
     * Ostatní pozice pole zůstavají prázdné
     * Ze všech sloupců se vyfiltrují ty sloupce, které nemají v profilech uložený 'order'
     * Tyto sloupce se postupně zařadí na volná místa pole
     */
    private _sortColumnsByOrder(ordArr: { column: Column; profile: IFrameColumnInfo }[]): Column[] {
        const sortedArr = new Array<Column>(this.dataGrid.columns.length);
        const profileOrderedColumns = ordArr.sort((a, b) => a.profile?.order - b.profile?.order);
        let orderDiff = 1;

        for (const item of profileOrderedColumns) {
            if (item?.column) {
                sortedArr[item.profile?.order - orderDiff] = item.column;
            } else {
                orderDiff++;
            }
        }

        // vyfiltruje všechny sloupce kromě těch, co jsou už seřazené
        const unorderedArr = this.dataGrid.columns.filter(col => !sortedArr.includes(col));

        // Na volná místa setřízeného pole se postupně zařadí sloupce, které nemají v profilu uvedený 'order'
        for (const [index, column] of sortedArr.entries()) {
            if (column == null && unorderedArr.length !== 0) {
                sortedArr[index] = unorderedArr.shift();
                sortedArr[index].order = index + 1;
            }
        }
        return sortedArr.filter((column) => {
            return column != null;
        });
    }

    //#region Properties
    /**
    * Indikuje zda je editovatelny seznam nebo seznam podporujici multieditsupport v editacnim rezimu
    */
    public override get isEditing(): boolean {
        return this._isEditing;
    }

    public override set isEditing(value: boolean) {
        if (!this._actualObjectStandardRights) {
            this._logger.error(`Frame '${this.frameName}' is having its property 'isEditing' changed to '${value.toString()}' but its rights hasn't been evaluated yet! Skipped!`); //`
            return;
        }

        // pokud není editace povolena právy, tak neumožníme přepnout do editace
        if (value
            && ((!this.actualObject && !this._actualObjectStandardRights.canEdit)
                || (this.actualObject
                    && ((!this._actualObjectStandardRights.canEdit && !this.actualObject.isNew)
                        || (!this._actualObjectStandardRights.canNew && this.actualObject.isNew))))) {
            return;
        }

        const setEditing = (value: boolean) => {
            this._isEditing = value;
            this.element.classList.toggle("is-editing", value);

            // Toolbar by měl být vyhodnocen už při zakládání kontextu
            if (!this.isEditContextInitiator) {
                this.setToolbarToEditMode(value);
            }

            if (this.dataGrid instanceof IEditableListDataGrid && !(this.isMultiSelecting && value)) {
                this.dataGrid.isInEdit = value;
            }

            this.editModeChanged.trigger(this, value);
        };

        if (!this.isActive) {
            if (this.hasOpenedDetailInModal) {
                this._isEditing = value; //Frame není na posledním modálním modálním okně, ale i tak mu musím předat informaci, že ho edituji prostřednicvím modálu
                if (!value || this.isEditContextInitiator)
                    this.editModeChanged.trigger(this, value); // Při otevřeném modálu a editačním kontextu je nutné přejít do editace i na něm
            }
            //Pokud je frame interaktivní, tak umožníme přechod do editace u nového záznamu nebo vypnout editaci
            if ((this.interactive || this.detail?.ownerForm.visibleOnlyDetail) && (!value || (this._actualObjectStandardRights.canNew && this.actualObject?.isNew))) {
                setEditing(value);
            }

            return;
        }

        if (this._isEditing === value) {
            if (this.detail && this.detail.isEditing !== value)
                this.editModeChanged.trigger(this, value);
            return;
        }

        setEditing(value);
    }

    public set headerColor(value: string) {
        this._color = value;
        if (this.dataGrid && this.dataGrid.headerRow)
            this.dataGrid.headerColor = value;
        if (this.menu)
            this.menu.setProperty(FormUICustomProperty.pageColor, value);
    }

    public get headerColor(): string {
        return this._color; //this.dataGrid.Header.Color;
    }

    public get currentDataGrid(): IDataGrid<IDataRowElement> {
        return this._currentDataGrid;
    }

    public get defaultFilterId(): string | null {
        return this._defaultFilterId;
    }

    public get isSelectedAll(): boolean {
        return this.currentDataGrid.isSelectedAll;
    }

    public get selectedIds(): string[] {
        return this.currentDataGrid.selectedRows.map(rowElement => rowElement.dataKey);
    }

    public get unselectedIds(): string[] {
        return this.currentDataGrid.dataRows
            .filter(rowElement => !this.currentDataGrid.selectedRows.includes(rowElement))
            .map(rowElement => rowElement.dataKey);
    }

    public get loadingCondition(): string {
        return this.dataSource.condition;
    }

    public get loadingConditionParams(): readonly ConditionParam[] {
        return this.dataSource.conditionParams;
    }
    //#endregion Properties

    //#region Constructors
    constructor(params: FormControlWithLoggerParams) {
        super(params);

        /**
         * V konstruktoru framu může být jen jednoduchá inicializace hodnot (z definice a pod.)
         * Logika a asynchronní procesy musí být až v inicializační metodě (initialize()),
         * která je vyvolávaná v potřebném pořadí a dá se na její doběhnutí čekat
         */

        this.element.classList.add("ListFrame", "IMasterableFrame", "dataGrid");

        this.masterAttr = this.element.getAttribute("data-masterattr");
        this.detailForm = this.element.getAttribute("data-detailform");
        this.isEditable = DataConverter.toBoolean(this.element.getAttribute("data-IsEditable"), false);
        this._autoRefreshInterval = DataConverter.toNumber(this.element.getAttribute("data-AutoRefresh"));
        this._autoRefreshSlave = DataConverter.toBoolean(this.element.getAttribute("data-autorefreshslave"));
        this._addAttrsListMethod = this.element.getAttribute("data-addAttrsListMethod");

        const ordering = this.element.getAttribute("data-orderby");
        this.orderBy = FrameSharedMethods.parseOrderBy(ordering, ListDataGrid.getDefaultOrderByDefinition(this.dataSource));
        this._definitionOrderBy = [...this.orderBy];

        this._frozenColumnCount = DataConverter.toNumber(this.element.getAttribute("data-FrozenColumnCount"));

        this._hideFilterRow = DataConverter.toBoolean(this.element.getAttribute("data-HideFilterRow"), false);
        this.disableSort = DataConverter.toBoolean(this.element.getAttribute("data-DisableSort"), false);

        this.editStyle = this.element.getAttribute("data-EditStyle") == null || this.element.getAttribute("data-EditStyle").toLowerCase() == "esmulti"
            ? EditStyleType.esMulti
            : EditStyleType.esSingle;

        this._openMode = FrameSharedMethods.getOpenModeTypeFromAttribute(this.element);

        this.condition = this.element.getAttribute("data-condition");
        const condParamsString = this.element.getAttribute("data-conditionparamsstring") || this.element.getAttribute("data-conditionparams");
        if (condParamsString)
            this.conditionParamsAttrs = condParamsString.split(";");

        this._selectedRecordChangeDeferred.resolve(false);
        this.isInitExecuteFilter = false;
        this._formObjectName = this.element.getAttribute("data-formobjectname");
    }
    //#endregion Constructors

    //#region Methods
    /** Vrací objekt, který obsahuje informace o prvku */
    public async getControlInfo(childInfo: Partial<IControlInfoObject>, clickedControl: IGenericControl): Promise<Partial<IControlInfoObject>> {
        return FrameSharedMethods.getControlInfo(this, childInfo, clickedControl);
    }

    protected _setCurrentDataGrid(dataGrid: IDataGrid<IDataRowElement>): void {
        this._currentDataGrid = dataGrid;
    }

    /**
     * Aktualizuje {@link _lastSelectedRecordId} v případě že na záznamu došlo
     * ke změne `dataKey` z původního instanceID na databázové ID.
     *
     * Změna `dataKey` se děje při uložení nového záznamu. Změnu je třeba reflektovat i do `_lastSelectedRecordId` kvůli správnému fungování metody {@link onSelectionChanged}
     * @param actualObject Právě uložený objekt.
     */
    protected _updateLastSelectedRecordID(actualObject: OWObject): void {
        if (this._lastSelectedRecordId === actualObject.instanceID) {
            this._lastSelectedRecordId = String(actualObject.id);
        }
    }

    /**
     * Znicializuje celý frame. Musí být zavoláno před jeho prvním použitím. Je možné spouštět asynchronní procesy.
     */
    protected override async _initializeVisual(): Promise<void> {
        if (this._initializationStarted) // inicializace může proběhnout jen jednou
            return;
        this._initializationStarted = true;

        /**
         * V této metodě by neměl být žádný výkonný kód.
         * Mělo by zde být pouze volání metod inicializujících jednotlivé oblasti a takovém pořadí, jaké je obecně potřeba
         */

        try {
            UIManager.instance.showLoader();

            // obecná inicializace frame
            await this._initializeFrame();
            // navázání listenerů
            this._initializeListeners();
            // vytvoření a nastavení DataSource
            await this._initializeDataSource();
            // vytvoření a nastavení DataGrid
            await this._initializeDataGrid();

            // vytvoření menu
            await this._initializeFormUI();

            // zpracování a zaregistrování akcí do menu (má smysl spouštět až je seznam vidět)
            await this._initializeActions();
            // načtení práv
            await this._initializeRights();

            this._isInitialized = true;
            this.initialized.trigger(this, undefined);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /** Nastavení detailu podle formu/mastera */
    protected override async _initializeFrame(): Promise<void> {
        await super._initializeFrame();

        // Popisek seznamu
        this._createFrameCaption();
    }

    protected override _initializeListeners(): void {
        super._initializeListeners();

        if (this.masterFrame instanceof GenericListFrame) {
            this._events.add(this.masterFrame.setObjectInitiated, () => {
                if (this.actualObject) { // Bez objektu není na co čekat -> uživatel nemůže nic prokliknout a není potřeba loader
                    // Při začátku načítání objektu na nadřízeném framu nastavím nový deferred, protože se zde bude taky manipulovat s AO a po tu dobu je třeba zobrazovat loader
                    this._startActualObjectSetDeferred();
                }

                this.setObjectInitiated.trigger(this, undefined);
            });
        }

        this._events.add(this.editModeChanged, async (sender, value) => await this._editModeChanged(sender, value));

        if (this._autoRefreshInterval > 0)
            this._activateAutoRefresh();

        this._events.add(this.actualObjectChanged, async (_masterableFrame: IMasterableFrame, eventArgs: IActualObjectChangedEventArgs) => {
            const wfOpenFormKey = "genericListFrameWfOpenForm";
            this._events.removeByKey(wfOpenFormKey);
            // naslouchání eventu wfOpenFormReceived pro otevření formuláře z WF; pomocí DataEvents nahrazuje použítí atributu "BLDialogFormName"
            if (eventArgs && eventArgs.actualObject && this._actualObjectHasWF) { // jen pokud má objekt workflow
                this._events.addWithKey(wfOpenFormKey, this._workflowManager.openFormReceived, (_sender, openFormEventArgs) => this._wfOpenFormHandler(openFormEventArgs, this));
            }
            this._updateRowData();
        });

        this._loadFrameData();
        this._events.add(this.ownerForm.stateChanged, (_sender, state) => {
            if (this.dataGrid && state.state === WindowState.visible || state.state === WindowState.realVisibility && state.value)
                this.dataGrid.resumeLoading();
        });

        this._events.add(this.contextCreated, async (_sender, value) => {
            this.isEditing = value;
            await this._triggerRightsChecking(ControlRightTypesGroup.inEdit);
        });
    }

    /** Stará se o automatické načtení dat po inicializaci datagridu, pokud je seznam masterem */
    protected _loadFrameData(): void {
        if (this.masterFrame) {
            return;
        }

        // Data se načítají až po vytvoření datagridu v případě, že tento seznam nemá masterFrame. Pokud masterFrame má, pak se spustí načítání až když je v masterFramu vybrán záznam.
        this.initialized.add(async () => {
            const loadData = async () => {
                if (!this.ownerForm.getState(WindowState.active))
                    return;

                this.ownerForm.stateChanged.remove(loadData);

                // Na tento await spoléhá TLF
                await this.waitForDataLoad();
                await this.checkIfIsCompact();
                await this.dataSource.loadNewData(undefined, this._cancelPrevAndCreateDataLoadingCTS().token);
                await this._checkInsertModeAndCreateNewRecord();
                this._conditionUpdated = false;
            };

            // provede se po inicializaci a nastavení viditelnosti datagridů
            if (this.ownerForm.getState(WindowState.active))
                this.dataGridInitialized.add(loadData);
            else
                this.ownerForm.stateChanged.add(loadData);
        }, true);
    }

    /**
     * Reaguje na změnu stavu editovatelnosti
     * @param _sender Kdo si změnu žádá
     * @param value Jaký je požadovaný stav
     */
    protected async _editModeChanged(_sender: GenericListFrame, value: boolean): Promise<void> {
        if (!value) {
            await this._newEvaluateRightsGroup(ControlRightTypesGroup.attributeChange);
        }
    }

    protected abstract _initializeDataSource(): Promise<void>;

    protected abstract _initializeDataGrid(): Promise<void>;

    public setDefaultFilterId(filterId: string | null): boolean {
        if (!this.isInitialized) {
            this._defaultFilterId = filterId;
            return true;
        }

        return false;
    }

    /** Umožňuje čekat na načtení dat
     * Vrací true, jakmile je načtena první várka dat
    */
    public override waitForDataLoad(): Promise<boolean> {
        return this._dataLoadingDefferred ? this._dataLoadingDefferred.promise : Promise.resolve(false);
    }

    /** Umožňuje čekat na dokončení načítání dat
     * Vrací true, jakmile jsou načtena všechna data, která mají být aktuálně načtena
    */
    public waitForDataLoadingEnded(): Promise<boolean> {
        return this._dataLoadingEndedDefferred ? this._dataLoadingEndedDefferred.promise : Promise.resolve(false);
    }

    protected _waitForColumnLoad(): Promise<boolean> {
        return this._columnLoadingFinished.promise;
    }

    public onlyListFrameInteractive(interactive: boolean): void {
        if (this.dataGrid)
            this.dataGrid.interactive = interactive;
        if (this.compactDataGrid)
            this.compactDataGrid.interactive = interactive;
    }

    public override prevRow(): void {
        if (this.isActive) {
            this.dataGrid.prevRecord();
        }
    }

    public override nextRow(): void {
        if (this.isActive) {
            this.dataGrid.nextRecord();
        }
    }

    /**
     * Označí záznam s předaným dataKey (většinou ID)
     * @param dataKey DataKey žádáného záznamu
     * @param deselectCurrent Příznak, zda se mají odznačit aktuálně vybrané záznamy
     * @param dontScrollIntoView Příznak, že se nemá zascrollovat na nově označený záznam
     */
    public async selectRow(dataKey: string, deselectCurrent?: boolean, dontScrollIntoView?: boolean): Promise<boolean> {
        UIManager.instance.showLoader();
        if (deselectCurrent) {
            while (this.currentDataGrid.selectedRows.length > 0)
                this.currentDataGrid.selectedRows[0].isSelected = false;
        }
        const dataRow = await this.currentDataGrid.selectRow(dataKey);
        if (dataRow && !dontScrollIntoView)
            dataRow.scrollIntoView({ behavior: "auto", block: "nearest", inline: "nearest" });
        UIManager.instance.hideLoader();

        return dataRow != null;
    }

    /**
     * Pokusí se v DataGridu najít záznam s předaným dataKey a označit jej. Pokud nebyl nalezen, nejdřív se dočte a vloží.
     * @param dataKey DataKey žádáného záznamu
     * @param deselectCurrent Příznak, zda se mají odznačit aktuálně vybrané záznamy
     * @param dontScrollIntoView Příznak, že se nemá zascrollovat na nově označený záznam
     */
    public async loadAndSelectRecord(dataKey: string, deselectCurrent?: boolean, dontScrollIntoView?: boolean): Promise<void> {
        await this.waitForDataLoadingEnded();

        if (ObjectUtils.isIdTemporary(dataKey)) {
            if (this.currentDataGrid.dataSource instanceof EditableDBDataSource) {
                await this.currentDataGrid.dataSource.newRow(undefined, undefined, undefined, undefined, dataKey);
            }
            return;
        }

        const desiredRow = this.currentDataGrid.dataRows.find((row) => row.dataKey == dataKey);

        if (!desiredRow) {
            if (this.dataSource instanceof DBDataSource) {
                const missingRow = await (this.dataSource as DBDataSource).loadSpecificRecord(dataKey);
                if (missingRow) {
                    // musime znova projit dataRow, pokud v nem neni, je sice z ds nacteny, ale jeste nebyl zobrazen, protoze se k nemu nedoscrolovalo (fetchCount)
                    if (!this.currentDataGrid.dataRows.some((row) => row.dataKey == dataKey)) {
                        this.currentDataGrid.insertExistingRecord(missingRow);
                    }
                    await this.selectRow(dataKey, deselectCurrent, dontScrollIntoView);
                } else {
                    this._lastSelectedRecordId = GenericListFrame._NO_ID;
                    this.clearActualObject();
                }
            }
        } else {
            await this.selectRow(desiredRow.dataKey, deselectCurrent, dontScrollIntoView);
        }
    }

    /**
     * Otevře detail do nového okna nebo modálu
     * @param openInEdit Zda má být otevřen v režimu editace
     * @param objID ID otevíraného objektu
     * @param showOnMainForm Zda má být otevřen do nového okna
     */
    protected async _openDetailSeparately(opt: OpenModalDetailOption = {}): Promise<void> {
        const { openInEdit, objID, showOnMainForm, openInMultiEdit, openMode } = { ...{ openInEdit: !this.hideButtons.edit, objID: null, showOnMainForm: false, openInMultiEdit: false, openMode: OpenModeType.standard }, ...opt };

        if (this.hasOpenedDetailInModal || !this.interactive) {
            this._openDetailInNewWindow = false;
            return;
        }

        let actualObject = this.actualObject;

        UIManager.instance.showLoader();
        try {
            if (openMode !== OpenModeType.insert && (!actualObject || (objID && (!actualObject || actualObject.id != objID)))) {
                try {
                    await EventAwaiter.createEventAwaiter(this.actualObjectSet, 10000);
                } catch (ex) {
                    // do nothing.
                }
                await this.waitForDataLoad();
                await this.waitForActualObjectSet();
                if (!this.actualObject) {
                    this._logger.error("Missing actualObject for detail form or timeouted. Detail form will not be opened.");
                    return;
                }
                actualObject = this.actualObject;
            }
            await this.rightsEvaluationTask;

            //pokud nemám právo editovat tak se ani nepokusím otevřít detail do editace
            const afterRightOpenInEdit = !this._actualObjectStandardRights.canEdit ? false : openInEdit;

            if (afterRightOpenInEdit) {
                if (!await this.lockObject(actualObject))
                    return;
            }

            let formInfo: IImplicitFormInfo;
            try {
                formInfo = await FrameSharedMethods.getDefaultFormName(this.detailForm, this.ownerForm, actualObject, this.frameContext, this._formObjectName);
            } catch (e) {
                ErrorHandler.instance.handleError(e);
                return;
            }

            let purposeType: MenuType | undefined = undefined;
            if (formInfo.PurposeType != null) {
                purposeType = convertFormOpenTypeToPurposeType(formInfo.PurposeType);
            }

            const masterAttr = formInfo.MasterAttr;
            let masterObject: OWObject = actualObject;
            let detailFormObject = masterAttr ? null : actualObject; // předává do otevíraného formuláře instanci objektu, který se má zobrazit v detailu. Pokud je nastaven masterAttr, tak je detail otevírán nad třídou objektu, který je vazbě. Do detailu by se tedy předával objekt jiné třídy než je ten ve vazbě masterAttr
            let masterLinkToAttr = formInfo.DetailAssocName;

            if (this._formObjectName) {
                const detailObject = await actualObject.getValue(this._formObjectName);
                if (detailObject instanceof OWObject) {
                    detailFormObject = detailObject;
                    masterObject = actualObject;
                    masterLinkToAttr = this._formObjectName;
                }
            } else {
                if (masterAttr != null) {
                    await OWManager.publishInstance(actualObject.instanceID, masterAttr, this._getRequestContext());
                    masterObject = AttrValueReader.read(await actualObject.getValue(masterAttr));
                }
                masterObject = formInfo.DetailAssocName && masterObject; // Předávat jen pokud je k dispozici DetailAssocName
            }

            const params: IFRMParams = {
                ownerForm: showOnMainForm ? null : this.ownerForm, // Předává se ownerForm, aby na něm bylo možné zakázat tlačítko Zpět a zaregistrovat nového potomka
                viewMode: !this.dataGrid.interactive,
                detailFormMasterFrame: masterAttr ? undefined : this, //Pokud je nastaven masterAttr tak se detailový formulář otevírá nad objektem, který je ve vazbě aktuálně označeného záznamu. Není tedy nad stejnou třídou jako listframe, ze kterého otevírám detail (proto otevíraný detail nemůže mít jako masterframe aktuální ListFrame)
                hideWindowActions: true,
                openInEdit: this.isEditing || this._openDetailInNewWindow || afterRightOpenInEdit,
                detailFormObject: detailFormObject,
                masterObject: masterObject,
                masterLinkToAttr: masterLinkToAttr,
                purposeType: purposeType,
                readVersioned: this.readVersioned,
                isImplicit: FrameSharedMethods.isFormNameImplicit(this.detailForm),
                formContext: this.ownerForm.formContext,
                multiEditMode: openInMultiEdit,
                openMode: openMode,
                insertObjectName: openMode === OpenModeType.insert ? this.objectName : "",
                selectedID: openMode === OpenModeType.insert ? objID : undefined,
            };

            // Příznak `hasOpenedDetailInModal` se kvůli načítání dílčích metod někdy nastaví opožděně.
            // To umožní nežádoucí znovuotevření formuláře. Kontrola je tedy nutná jako na začátku metody, tak i zde.
            if (!this.hasOpenedDetailInModal) {
                if (showOnMainForm) {
                    await FormManager.instance.openForm(formInfo.FormName, params);
                } else {
                    try {
                        this.hasOpenedDetailInModal = true;
                        const onClose = () => {
                            this.hasOpenedDetailInModal = false;
                            this.openedModalMasterableFrames = null;
                        };

                        const openedForm = await FormManager.instance.openFormModal({ formName: formInfo.FormName, parametricData: params, onClose, type: ModalWindowType.parametricForm, parentFormId: this.ownerForm.formInstanceId }) as IForm; //TODO: pridat do toho enumu i DetailFormAction?
                        this.openedModalMasterableFrames = openedForm?.masterableFrames;
                    } catch (error) {
                        this.hasOpenedDetailInModal = false;
                        throw error;
                    }
                }
                this._openDetailInNewWindow = false;
            }
        } catch (err) {
            ErrorHandler.instance.handleError(err);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /**
     * Provede uložení záznamů
     * @param actualObjectOnly Zda má být uložený pouze aktuálně vybraný záznam
     */
    protected abstract _saveRecords(actualObjectOnly?: boolean): Promise<boolean>;

    public createSelectionObjectList(): OWObjectList {
        const objList = new OWObjectList();
        objList.className = this.objectName;
        const idsThreshold = AppContext.instance.configuration.forceIdsInSelectAllConditionThreshold;

        // all items are selected AND not all records have been loaded or the limit of selected records to create IN-condition has been exceeded
        if (this.dataGrid.isSelectedAll && (!this.dataGrid.allDataRead || this.dataGrid.dataRows.length > idsThreshold)) {
            objList.isSelectAll = true;
            objList.condition = this.loadingCondition;
            objList.conditionParams = this.loadingConditionParams;
            this.unselectedIds.forEach(id => objList.add({ id: id, className: this.objectName }));
        } else {
            this.selectedIds.forEach(id => objList.add({ id: id, className: this.objectName }));
        }
        return objList;
    }

    /** Spustí mazání aktuálně vybraných záznamů */
    public async deleteRecords(): Promise<void> {
        const result = await this._confirmDeleteDialog();

        if (result.result !== MessageButtonType.ok) {
            return;
        }

        const objList = this.createSelectionObjectList();

        // Je potřeba přidat podmínky z práv na mazání, nejdříve pro defaultní roli
        const formRightWithoutRole = this.ownerForm.formRights.find(right => right.roleID === ROLELESS_ROLE_ID);
        let condition: string;

        if (formRightWithoutRole && formRightWithoutRole.deleteConditionID) {
            condition = FrameSharedMethods.combineRightCondition(condition, formRightWithoutRole.getDeleteRight());
        } else { // A pokud nemá podmínku nebo není, tak z ostatních
            for (const right of this.ownerForm.formRights) {
                if (right.roleID !== ROLELESS_ROLE_ID && right.deleteConditionID) {
                    condition = FrameSharedMethods.combineRightCondition(condition, right.getDeleteRight());
                }
            }
        }

        try {
            UIManager.instance.showLoader();

            const result = await OWManager.deleteObjects(this.ownerForm.formName, objList, condition, this._getRequestContext(), await FrameSharedMethods.createAfterCreateExtendedOperations(this._createExtendedOperationsParams(), false));

            if (result.isInLongTransaction) {
                new MessageWindow(new LocalizedStringBuilder("GUI-696483")).show(); // Mazání bylo spuštěno na pozadí. O jeho výsledku budete informování notifikací.
                return;
            }

            UIManager.instance.showResultMessages(result.messages);

            await this.refreshData(false);
            await this.dataSource.loadSpecificRecords(result.failedIds, true);
            await this.waitForDataLoad();

            this.setToolbarToMultiselectMode(this.currentDataGrid.selectedRows.length > 1);
        } catch (error) {
            ErrorHandler.instance.handleError(error);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /**
     * Nastaveni akci pro Toolbary
     */
    protected override async _initializeFormUI(): Promise<void> {
        this.createMenu();

        if (!this.menu)
            throw new InvalidOperationException("Unable to create frame menu");

        this._events.add(Form.formLayoutChanged, () => {
            if (this.dataGrid)
                this.dataGrid.resizeGrid();
        });

        // Vybrani zalozky pri kliku do seznamu
        this._events.addListener(this.element, "click", () => {
            if (!this.hideToolBar)
                this.menu.setProperty(FormUICustomProperty.selected, true);
            // zruseni vsech seznamu oznacenych jako aktivnich
            const list = Array.from(this.ownerForm.element.querySelectorAll(".ListFrame")) as Array<HTMLElement>;
            for (const el of list) {
                el.classList.remove("Active");
            }
            this.element.classList.add("Active");
            this._filterManager?.updateFilterPanel();
        });
        this._events.addListener(this.element, "contextmenu", (e: MouseEvent) => {
            let dataRow: DataRow;
            let currentElem = e.target;
            while (currentElem) {
                if (currentElem instanceof DataRow) {
                    dataRow = currentElem;
                    break;
                }

                currentElem = currentElem instanceof HTMLElement && currentElem.parentElement || null;
            }

            this.contextMenuOpening.trigger(this, { dataKey: (dataRow != null && dataRow.dataKey) || null, originalEvent: e });
        });

        if (!this.isMainFrame)
            this.headerColor = Pages.getColor();

        // když ještě není dataGrid vytvořený, tak se registrace naplánuje až po jeho vytvoření
        this._events.add(this.dataGridInitialized, () => {
            const factory = (dataKeyFetcher: () => string) => this.menu.renderRecordControls(dataKeyFetcher);
            if (this.dataGrid)
                this.dataGrid.registerRowControlsFactory(factory);
            if (this.compactDataGrid)
                this.compactDataGrid.registerRowControlsFactory(factory);
        });

        // toolbar musí být vidět, pokud není nastaveno, aby byl skrytý a zároveň je vidět seznam nebo seznam má detail, který je vidět
        const updateMenuVisibility = () => this.menu.setVisibility(!this.hideToolBar && (this.canValueLoad || (this.detail != null && this.detail.canValueLoad)));
        updateMenuVisibility();

        // Skryvani a zobrazování toolbaru pri vstupu na frame
        this._events.add(this.canValueLoadChanged, () => {
            updateMenuVisibility();
        });
    }

    /**
     * Pokud není vytvořeno menu, tak jej vytvoří. V opačném případě nedělá nic - Tato možnost zavedena pro TreeFrame, kde se do menu GLF přiřadí menu TF již před inicializací GLF.
     */
    public createMenu(): void {
        if (!this.menu) {
            this.menu = this.ownerForm.formUI.createFrameMenu(this);
        }
    }

    protected override async _initializeActions(): Promise<void> {
        await super._initializeActions();
        await this._registerRecordActions();
        await this._addFrameChoiceActions();
        // Při skrytém toolbaru nebudeme přidávat akce
        if (!this.hideToolBar) {
            await this._addStandartActions(this.isMainFrame);

            this.addCustomActions();
        }

        await this._newEvaluateRights([...RightGroups.getControlRightGroupTypes(ControlRightTypesGroup.IED)]); // frame se může vytvářet i v náhledovém režimu a ten se musí uplatnit

        this._events.add(this.initialized, () => this._newEvaluateRightsGroup(ControlRightTypesGroup.initialized));

        // Operace prováděné po prvním načtení dat
        const onDataFirstLoadedKey = "genericListFrameOnDataFirstLoaded";
        this._events.addWithKey(onDataFirstLoadedKey, this.dataLoaded, async () => {
            if (!this._firstLoadFinished) {
                this._firstLoadFinished = true;

                if (!this.element.classList.contains("data-loaded"))
                    this.element.classList.add("data-loaded");

                this._events.removeByKey(onDataFirstLoadedKey);
            }
        });

        if (this.isEditable && !this.isMainFrame)
            this.setToolbarToEditMode(this.isEditing);

        this.interactive = !this.ownerForm.readOnly;

        if (this.detail) {
            this.detail.hasSplitter = this.visible;
        }

        if (await this._getClassEncryptingAllowed())
            this._registerDecryptButton();
    }

    protected async _checkInsertModeAndCreateNewRecord(): Promise<boolean> {
        if (this.isMainFrame && this.ownerForm.openMode === OpenModeType.insert) {
            if (await this.newRecord(undefined, true)) {
                await this.ownerForm.setToolbarToEditMode(true);
            }
            return true;
        }
        return false;
    }

    protected async _registerRecordActions(): Promise<void> {
        if (this.detailForm) {
            const openDetailAction = FrameSharedMethods.createStandardRecordAction(ActionType.standardOpenDetail, async (e: Event, dataKey: string) => {
                EventHelper.stopPropagation(e);

                const row = this.currentDataGrid.dataRows.find((row) => row.dataKey === dataKey);

                // zkontroluje se uložení změn
                this.showSaveChangesDialog(async () => {
                    if (row.isDeleted) {
                        return;
                    }

                    if (this.currentDataGrid.selectedRows.length !== 1 || this.currentDataGrid.selectedRows[0].dataKey !== row.dataKey) {
                        await this.loadAndSelectRecord(dataKey, true, true);
                    }

                    return await this._openDetailSeparately({
                        openInEdit: this._openMode === OpenModeType.edit,
                        objID: row.dataKey,
                    });
                });
            }, this);

            this.menu.addAction(openDetailAction);
        }

        let action: IAction;
        const systemOperationRights = DIContainer.instance.create(ISystemOperationRights);
        if ((action = FrameSharedMethods.createStandardAction(ActionType.standardInfoRecord, async () => {
            await this._infoRecordDialog();
        }, this)) != null) {
            action.toMenu = MenuType.mtInfo;
            this.menu.addAction(action);
        }

        if (this._showAuditData && (await systemOperationRights.get(SystemOperation.SHOWAUDITDATA)) && (action = FrameSharedMethods.createStandardAction(ActionType.standardAuditInfoRecord, async () => {
            await this._auditRecordDialog();
        }, this)) != null) {
            action.toMenu = MenuType.mtInfo;
            this.menu.addAction(action);
        }

        if ((await systemOperationRights.get(SystemOperation.SHOWWFHISTORY)) && (action = FrameSharedMethods.createStandardAction(ActionType.standardShowWFHistory, async () => {
            await this._wfHistoryDialog();
        }, this)) != null) {
            action.toMenu = MenuType.mtInfo;
            this.menu.addAction(action);
        }

        if ((await systemOperationRights.get(SystemOperation.SHOWWFPARTICIPANTS)) && (action = FrameSharedMethods.createStandardAction(ActionType.standardShowWFParticipants, async () => {
            await this._wfParticipantsDialog();
        }, this)) != null) {
            action.toMenu = MenuType.mtInfo;
            this.menu.addAction(action);
        }

        if ((await systemOperationRights.get(SystemOperation.SHOWWFSTATUS)) && (action = FrameSharedMethods.createStandardAction(ActionType.standardShowWFStatus, async () => {
            await this._openWfStatusViewer();
        }, this)) != null) {
            action.toMenu = MenuType.mtInfo;
            this.menu.addAction(action);
        }
    }

    /**
     * Zobrazení dialogu s informacemi o záznamu
     */
    protected async _infoRecordDialog(): Promise<void> {
        const params: IFRMParams = {
            autoSelectFirstRow: true,
            masterObject: this.actualObject,
            masterLinkToAttr: "Objekt",
            ownerForm: this.ownerForm, // Aby na něm bylo možné zakázat tlačítko Zpět
            formContext: this.ownerForm.formContext,
        };
        await FormManager.instance.openFormModal({ formName: "FRM.SZakladni_info.SZakladni_info", parametricData: params, type: ModalWindowType.parametricForm });
    }

    /**
     * Zobrazení dialogu s auditními daty o záznamu
     */
    protected async _auditRecordDialog(): Promise<void> {
        const params: IFRMParams = {
            autoSelectFirstRow: true,
            masterObject: this.actualObject,
            condition: `ID_Zaznam = ${this.actualObject.id} and Trida_zaznamu.Nazev_tridy = '${this.actualObject.objectName}'`,
            ownerForm: this.ownerForm, // Aby na něm bylo možné zakázat tlačítko Zpět
            formContext: this.ownerForm.formContext,
        };
        await FormManager.instance.openFormModal({ formName: "FRM.DAUDIT.DAUDIT", parametricData: params, type: ModalWindowType.parametricForm });
    }

    /**
     * Zobrazení historie WF
     */
    protected async _wfHistoryDialog(): Promise<void> {
        const params: IFRMParams = {
            masterObject: this.actualObject,
            condition: `ID <> 0 AND ( (Zobrazovat = null or Zobrazovat = true) AND ( Historie_WF = ${this.actualObject.id} AND Trida_Historie_WF.Nazev_tridy = '${this.actualObject.objectName}') )`,
            ownerForm: this.ownerForm, // Aby na něm bylo možné zakázat tlačítko Zpět
        };
        await FormManager.instance.openFormModal({ formName: "FRM.WF Historie.WF Historie", parametricData: params, type: ModalWindowType.parametricForm });
    }

    /**
     * Zobrazení dialogu s přehledem hráčů
     */
    protected async _wfParticipantsDialog(): Promise<void> {
        const params: IFRMParams = {
            autoSelectFirstRow: true,
            masterObject: this.actualObject,
            condition: `ID_doklad_hracu = ${this.actualObject.id} and Trida_dokladu.Nazev_tridy = '${this.actualObject.objectName}'`, //`
            ownerForm: this.ownerForm, // Aby na něm bylo možné zakázat tlačítko Zpět
            formContext: this.ownerForm.formContext,
        };
        await FormManager.instance.openFormModal({ formName: "FRM.SPrehled_hracu.SPrehled_hracu", parametricData: params, type: ModalWindowType.parametricForm });
    }

    /**
     * Zobrazení instance editoru WF v read-only režimu
     */
    protected async _openWfStatusViewer(): Promise<void> {
        const params: IFRMParams = {
            masterObject: this.actualObject,
            ownerForm: this.ownerForm, // Aby na něm bylo možné zakázat tlačítko Zpět
            setValues: CONST_STATUS_VIEW_ENABLED,
        };
        await FormManager.instance.openFormModal({ formName: "FRM.SWorkflow.editor", parametricData: params, type: ModalWindowType.parametricForm });
    }

    protected _registerDecryptButton(): void {
        this._decryptItemAction = FrameSharedMethods.createDecryptToolbarAction(() => this.decryptObject(), this);
        this.menu.addAction(this._decryptItemAction);
    }

    protected async _addStandartActions(isMainFrame: boolean): Promise<void> {
        //registrace akce refresh na toolbaru
        //Prvni cast podminky kontroluje zda je tlacitko viditelne. Druha cast rika, ze pokud se seznam lezi na formulari, ktery ma nastaveno VisibleOnlyDetail
        //a soucasne jeho parent je form, tak akci nezaregistruje a nechaji, aby si ji zaregistroval DetailFrame. Je to z duvodu refreshe na modalnim okne, ktere nema
        //viditelny seznam. V tomto pripade je detail navazan na seznam, ze ktereho bylo modalni okno otevrene a ne na skryty seznam na modalnim okne.
        if (!this.hideButtons.refresh && !(isMainFrame && this.ownerForm.visibleOnlyDetail) && this.visible) {
            this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardRefreshList, () => this.refreshOnClick(), this));
        }

        if (this.allowExport || this.allowReport || this.allowGraph || this.allowColumnSettings) {
            const data: IExportData = {
                frame: this,
                readVersioned: this.readVersioned,
            };

            if (this.allowReport) {
                FrameSharedMethods.createReportActions(data, this._getRequestContext());
            }

            if (this.allowExport) {
                FrameSharedMethods.createExportActions(data, this._getRequestContext());
            }
        }

        let action: IAction;
        if (!this.hideButtons.delete) {
            const action = FrameSharedMethods.createMultiSelectAction(ActionType.standardDeleteRecords, async () => {
                await this.deleteRecords();
            }, this);
            this.menu.addAction(action);
        }

        // Jestli na formuláři zobrazuje jen seznam, a editovat ho musí v samostatném okně (property "ShowOnlyOutside" na DetailFrame je True)
        if (isMainFrame && !this.ownerForm.visibleOnlyDetail && (this.ownerForm.detailFrame == null || this.ownerForm.detailFrame.masterFrame !== this)) {
            if (!this.hideButtons.insert) {
                action = this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardNewRecord, async () => {
                    await this.newRecord();
                }, this));
                action.enabled = false;
            }

            if (!this.hideButtons.copy) {
                this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardCopyRecord, async () => {
                    await this.createCopy();
                }, this));
            }

            if (!this.actualObjectStandardRights.canNew) {
                if (!this.hideButtons.insert) {
                    if ((action = this.menu.getAction(ActionType.standardNewRecord)) != null)
                        action.enabled = false;
                }
                if (!this.hideButtons.copy) {
                    if ((action = this.menu.getAction(ActionType.standardCopyRecord)) != null)
                        action.enabled = false;
                }
            }

            if (!this.hideButtons.edit) {
                this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardEditRecord, async () => {
                    await this.editRecord();
                }, this));
            }

            await this._createStandardSaveActionButtons();

            if (!this.hideButtons.cancel) {
                this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardCancelChanges, () => this.cancelChanges(), this));
            }

            if (!this.isEditing) {
                if (!this.hideButtons.save) {
                    if ((action = this.menu.getAction(ActionType.standardSaveRecord)) != null)
                        action.visible = false;
                    if ((action = this.menu.getAction(ActionType.standardSaveAll)) != null)
                        action.visible = false;
                }
                if (!this.hideButtons.cancel) {
                    if ((action = this.menu.getAction(ActionType.standardCancelChanges)) != null)
                        action.visible = false;
                    if ((action = this.menu.getAction(ActionType.standardCancelRecordChanges)) != null)
                        action.visible = false;
                }
            }

            if (!this.hideButtons.delete) {
                this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardDeleteRecord, async () => {
                    await this.deleteRecord();
                }, this));
            }
        }

        // Pokud má ListFrame vlastní FormUI (seznam na záložce) - tzn.ListFrame není mainFrame a nemá detail
        if (!isMainFrame && !this.detail) {
            if (this.isEditable) {
                if (!this.hideButtons.insert) {
                    action = this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardNewRecord, async () => {
                        await this.newRecord();
                    }, this));

                    action.enabled = false;
                }

                if (!this.hideButtons.copy) {
                    this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardCopyRecord, async () => {
                        await this.createCopy();
                    }, this));
                }

                if (!this.hideButtons.edit) {
                    this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardEditRecord, async () => {
                        await this.editRecord();
                    }, this));
                }

                if (!this.hideButtons.delete) {
                    this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardDeleteRecord, async () => {
                        await this.deleteRecord();
                    }, this));
                }

                await this._createStandardSaveActionButtons();

                if (!this.hideButtons.cancel) {
                    this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardCancelChanges, () => this.cancelChanges(), this));
                }
            } else {
                /**
                 * Jde o ObjectListEdit
                 */
                if (this.detailForm) {
                    if (!this.hideButtons.insert) {
                        action = this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardNewRecord, async () => {
                            await this.newRecord();
                        }, this));
                        action.enabled = false;
                    }

                    if (!this.hideButtons.copy) {
                        this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardCopyRecord, async () => {
                            await this.createCopy();
                        }, this));
                    }

                    if (!this.hideButtons.edit) {
                        this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardEditRecord, async () => {
                            await this.editRecord();
                        }, this));
                    }

                    if (!this.hideButtons.delete) {
                        this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardDeleteRecord, async () => {
                            await this.deleteRecord();
                        }, this));
                    }
                    /**
                     * Když detail může mazat, tak vývaří editační kontext. Proto potřebuje tlačítka na uložení a zahození kontextu.
                     * O jejich korektní zobrazení se starají práva.
                     */
                    await this._createStandardSaveActionButtons();

                    if (!this.hideButtons.cancel) {
                        this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardCancelChanges, () => this.cancelChanges(), this));
                    }
                }
            }
        }
    }

    /**
     * Přidá akce pro frame. Jejich registrace tak nepodlehá nastavení vlastnosti hideToolBar.
    */
    protected async _addFrameChoiceActions(): Promise<void> {
        this.menu.addAction(FrameSharedMethods.createRecordsCountAction(this, this.dataSource));

        if (this.allowGraph) {
            FrameSharedMethods.createGraphActions(this);
        }

        if (this.allowColumnSettings) {
            this.menu.addAction(this._createColumnSettingsAction());
        }

        await this._addShowSubRecordsAction();
    }

    /**
     * @internal
     */
    protected set _columnInfoBuilder(columnInfoBuilder: ColumnInfoBuilder) {
        this.__columnInfoBuilder = columnInfoBuilder;
    }

    /**
     * @internal
     */
    protected get _columnInfoBuilder(): ColumnInfoBuilder {
        return this.__columnInfoBuilder;
    }

    /** Vytvoří akci, která zobrazí panel s nastavením sloupců */
    protected _createColumnSettingsAction(): StandardAction {
        const action = FrameSharedMethods.createStandardAction(ActionType.standardColumnSettings, async () => {
            const contentBuilder = new ColumnInfoBuilder(this.dataGrid.columns, this, () => this.ownerForm.checkAndSaveForm());

            contentBuilder.columnPositionChanged.add((_sender, data) => {
                const column = this.dataGrid.columns.find(col => col.attrName === data.attrName);
                // oldOrder z události počítá pouze s viditelnými sloupci, ale při aktualizaci pořadí sloupců v profilech je potřeba hodnota vztažená ke všem
                const oldOrder = this.dataGrid.columns.findIndex(col => col.attrName == column.attrName);

                this.dataGrid.setColumnOrder(column, data.newOrder, true);

                // newOrder z události počítá pouze s viditelnými sloupci, ale v profilu potřebujeme hodnotu vztaženou ke všem
                const newOrder = this.dataGrid.columns.findIndex(column => column.attrName === data.attrName) + 1;
                this._changeProfileColumnOrder(data.attrName, newOrder, oldOrder, false);
            });

            contentBuilder.columnVisibilityChanged.add((_sender, data) => {
                this._setProfileColumnInfoAttr(data.attrName, FrameColumnInfoAttributes.visible, data.visible, false);

                this.dataGrid.columns.find((column) => column.attrName === data.attrName).visible = data.visible;

                this.frameProfileChanged.trigger(this, null);
            });

            contentBuilder.columnWidthChanged.add((_sender, data) => {
                this._setProfileColumnInfoAttr(data.attrName, FrameColumnInfoAttributes.width, data.width, false);

                this.dataGrid.setColumnWidth(this.dataGrid.columns.find((column) => column.attrName === data.attrName), data.width);

                this.frameProfileChanged.trigger(this, null);
            });

            this._columnInfoBuilder = contentBuilder;
            this.ownerForm.formSettingsPanel.contentBuilder = contentBuilder;
            this.ownerForm.formSettingsPanel.titleBuilder = new LocalizedStringBuilder("GUI-578321"); // Nastavení seznamu

            this.ownerForm.formSettingsPanel.show();
        }, this);

        action.toMenu = MenuType.mtFrameMenu;

        return action;
    }

    /**
     * Zobrazí YNC dialog, pokud formulář obsahuje neuložené změny.
     * @param yesNoAction Akce, která je provedena pokud nebyl dialog zobrazen nebo je výsledek dialogu Y nebo N.
     */
    public async showSaveChangesDialog(yesNoAction: () => Promise<void>): Promise<MessageButtonType | null> {
        if (!(this.dataGrid instanceof IEditableListDataGrid)) {
            await this.ownerForm.checkAndSaveForm({ forceSave: this._suppressSaveDialog });
            await yesNoAction();

            return null;
        }

        if (this.checkAndSaveActualFrameOnly) {
            if (this.isEditing && this.dataGrid.editedRows.length > 0) {
                const result = await FrameSharedMethods.showSaveChangesDialog();

                if (result.result === MessageButtonType.ok) {
                    await this.saveRecords();
                    await yesNoAction();
                } else if (result.result === MessageButtonType.no) {
                    await this.cancelChanges();
                    this.isEditing = false;
                    await yesNoAction();
                }

                return result.result;
            } else {
                if (this.isEditing) {
                    this.isEditing = false; // i pokud nejsou žádné řádky změněny, tak musíme ukončit mód editace (reaguje na to i toolbar a případný detail)
                }

                await yesNoAction();
            }

            return null;
        }

        const result = await this.ownerForm.checkAndSaveForm({ cancelEditIfNotEdited: true, forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext });

        if (this.isEditing && this.dataGrid.editedRows.length > 0) {
            if (result) { //result = Y|N (změny uloženy nebo zahozeny)
                await yesNoAction();
            }
        } else {
            if (this.isEditing && !this.isEditContextInitiator) {
                this.isEditing = false; // i pokud nejsou žádné řádky změněny, tak musíme ukončit mód editace (reaguje na to i toolbar a případný detail)
            }

            if (result) { //result = Y|N (změny uloženy nebo zahozeny)
                await yesNoAction();
            }
        }

        return null;
    }

    /**
     * Zavola validaci na gridu pokud je editovatelny a je v editaci
     */
    protected async _validRowOnChange(notSelected: boolean = false, ct?: CancellationToken): Promise<void> {
        if (this.isEditable && this.isEditing) {
            await this._checkDataGridEmptyRequiredComponents(notSelected, ct);
        }
    }

    public async decryptObject(): Promise<void> {
        if (!this.actualObject)
            return;
        if (this.actualObject.isServerSideEncrypted && !this.actualObject.isEncryptionFinalized) {
            if (!PasswordManager.hasPassword(this.actualObject.objectName, this.actualObject.id)) {
                const result = await FrameSharedMethods.showEncryptDecryptDialog(this.actualObject, false);

                this._decryptItemAction.enabled = !result;
            }
        }
    }

    private _wfOpenFormHandler(args: IWFOpenFormEventArgs, frame: GenericListFrame): void {
        const message = args.message;

        if (!args.handled && message && message.message && message.message.body && frame.dataSource instanceof EditableDBDataSource) { // pouze pro EditableDBDataSource aneb ne pro OLE
            // TODO: Prozatím se z jednoho požadavku(jedna instance objektu) na otevření formulářů z WF zpracuje pouze první formulář. Chování při zpracování více zatím nebylo vymyšleno.
            // Pro úpravu odkomentovat následující for a upravit podle níže uvedených TODO; Bez dalších úprav by ale formuláře jen "naplácalo" na sebe
            //for (let item of message.Message.Body) {
            const body = message.message.body as IWFOpenFormParams[];
            const item: IWFOpenFormParams = body[0]; // TODO: viz TODO výše - odstranit tento řádek
            if (item == null || !(item instanceof IWFOpenFormParams)) {
                //this._logger.log(`WFOpenFormDataEvent: Item has already been read (removed) or is not in correct format.`); // `
                return;
            }
            const actualInstanceID = item.ActualInstanceID;
            if (frame.dataSource.loadedInstanceIDs.indexOf(actualInstanceID) === -1) {
                //this._logger.log(`WFOpenFormDataEvent: Instance ${actualInstanceID} not watched on GLF "${frame.frameName}" on Form ${frame.ownerForm.formName}"`); // `
                return;
            }
            body[0] = undefined; // pokud je instance na tomto GLF, víme, že nebude na jiném, aby se tedy na jiném zbytečně znovu nehledal; TODO: viz TODO výše "message.Message.Body[0]" nahradit za "item"
            //this._logger.log(`WFOpenFormDataEvent item processed: "${JSON.stringify(item)}" on GLF "${frame.frameName}" on Form "${frame.ownerForm.formName}"`); // `

            this._openFormFromWf(frame, item);
            //}
        }
    }

    /**
     * Otevře modální formulář z WF akce
     * @param frame
     * @param item
     */
    private _openFormFromWf(frame: GenericListFrame, item: IWFOpenFormParams): void {
        const formParameters: IFRMParams = {
            viewMode: false,
            inLOVMode: false,
            isModal: true,
            applyLOVCondition: false,
            autoSelectFirstRow: false,
            openInEdit: false,
            ownerForm: frame.ownerForm,
            executerFrame: frame,
            formContext: this.ownerForm.formContext,
        };

        switch (item.Purpose) {
            case 1:
                formParameters.purposeType = MenuType.mtPrint;
                break;
            case 2:
                formParameters.purposeType = MenuType.mtActions;
                break;
            default:
        }

        if (item.LinkAttr) {
            formParameters.masterObject = frame.actualObject;
            formParameters.masterLinkToAttr = item.LinkAttr;
        }

        if (item.Condition)
            formParameters.condition = item.Condition;

        FormManager.instance.openFormModal({ formName: item.FormName, parametricData: formParameters, type: ModalWindowType.parametricForm, showBreadcrumbNav: true, parentFormId: this.ownerForm.formInstanceId });
    }

    private async _createStandardSaveActionButtons(): Promise<void> {
        if (!this.hideButtons.save) {
            if ((await this.supportsEditContext) && this.useMasterContext || this.hasHandedContext) {
                this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardSaveRecordInEditContext, async () => {
                    await this._saveRecords();
                }, this));
            } else {
                this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardSaveAll, async () => {
                    await this._saveRecords();
                }, this));

                this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardSaveRecord, async () => {
                    await this._saveRecords(true);
                }, this));
            }
        }
    }

    protected override _setActualObject(actObj: OWObject, isFromMasterObjChanged: boolean = false): void {
        //this._logger.log("GenericListFrame.SetObj() - Name: " + this.FrameName + ": ", actObj == null ? "null" : actObj.ObjectName + ": " + actObj.ID);

        // je nezadouci proces zmeny objekt vyvolavat vickrat, kdyz uz tu ta stejna instance je, akorat tim vznika spousta dalsi zbytecnych ukonu
        if (this.actualObject === actObj) {
            if (this.dataGrid.isSelectedAll)
                this.setToolbarToMultiselectMode(this.currentDataGrid.selectedRows.length > 1);

            this._resolveActualObjectSetDeferred(true);
            return;
        }

        //mohlo dojít k refreshi objektu (např. po zřušení změn), je potřeba informovat příslušný dataRow, který může používat staré instanceID
        let actObjDataRow: IEditableListDataRowElement;
        if (this.dataGrid && actObj && (actObjDataRow = this.dataGrid.dataRows.find(i => i.dataKey == actObj.id))) {
            actObjDataRow.setInstance(actObj);
        }

        const actualObjectListenersKey = "genericListFrameActualObjectListeners";
        this._events.removeByKey(actualObjectListenersKey);

        const previousActualObject = this.actualObject;

        // V případě nastavování nullu z onMasterObjectChanged se dataGridu nastaví metoda, která defferred resolvuje v případě, že se po načtení nebude nastavovat objekt.
        if (actObj == null && isFromMasterObjChanged) {
            if (this.dataGrid instanceof DataGrid)
                this.dataGrid.setAfterFetchNoReselectCallback(() => {
                    if (this._dataLoadingDefferred)
                        this._resolveActualObjectSetDeferred(true);
                });
        }

        // Událostmi se do hloubky informují všechny podřízené framy, protože na nich také dojde ke změně objektu
        this.setObjectInitiated.trigger(this, undefined);

        //vypnuti vstupu do editace doubleclickem
        this.setToolbarToMultiselectMode(this.currentDataGrid.selectedRows.length > 1);
        if (actObj != null) {
            // Nějaká taková ochrana, kdyby náhodou od někud došlo (opožděně) k volání setu objektu, jehož id se liší od id vybraného řádku
            // Dochazí k přetypování stringu a čísla, proto !=
            // eslint-disable-next-line eqeqeq
            if (!this.isMultiSelecting && this.currentDataGrid.selectedRows.length > 0 && this.currentDataGrid.selectedRows[0].dataKey != actObj.id) {
                return;
            }

            super._setActualObject(actObj, isFromMasterObjChanged); // Musí být na předkovi(super), jinak by došlo k zacyklení

            if (this.actualObject != null) {
                this._events.addWithKey(actualObjectListenersKey, this.actualObject.attributeChanged, this._attributeChangedEventMethod);
                this._events.addWithKey(actualObjectListenersKey, this.actualObject.dataRefreshed, (sender: OWObject) => this._onObjectDataRefreshed(sender));
            }

            this._updateRowInfo();
            this.selectionChanged.trigger(this, { sender: this, objectName: actObj.objectName, actualObject: actObj });
            //this.triggerActualObjectChange(this.actualObject, previousActualObject);
            this.actualObjectSet.trigger(this, actObj);

            if (this._openDetailInNewWindow)
                this._openDetailSeparately();
        } else {
            super._setActualObject(null, isFromMasterObjChanged); // Musí být na předkovi(super), jinak by došlo k zacyklení
            this.selectionChanged.trigger(this, { sender: this, objectName: "", actualObject: actObj });
            //this.triggerActualObjectChange(this.actualObject, previousActualObject);
        }

        this.triggerActualObjectChange(this.actualObject, previousActualObject);
        // Pokud jde o set nullu z handleru onMasterObjectChanged (tzn. jedná se o podřízený frame), jde o vymazání hodnoty před jejím (možným) nastavením na (jinou) hodnotu,
        // proto nebudu označovat načítání jako dokončené. Tak bude označeno po nastavení nové hodnoty nebo v místech, kde už je jasné,
        // že k novému nastavení nedojde (např. this._updateConditionAndRead nebo DataGrid._showData; viz reference).
        if (actObj || !isFromMasterObjChanged) // ala !(actObj == null && isFromMasterObjChanged)
            this._resolveActualObjectSetDeferred(true);
    }

    protected override _keepUsedInstances(): void {
        const selectedInstances = this.currentDataGrid.selectedRows.map(r => InstanceManager.getInstance(r.dataset["instanceid"]));

        // zachovávají se instance buď jednoho právě označeného záznamu nebo všech vybraných
        const keepInstances = !this.isMultiSelecting ? (this._actualObject ? [this._actualObject] : []) : selectedInstances;

        //pokud je LF esMulti a zrovna má rozeditované záznamy, tak i ty musejí zůstat se stejným instanceID.
        if (this.editStyle == EditStyleType.esMulti && this._isEditing && this.currentDataGrid instanceof IEditableListDataGrid) {
            const changedRows: IDataRowElement[] = this.currentDataGrid.dataRows.filter(r => r instanceof IEditableDataRowElement && (r.isChanged || !r.isValid));
            changedRows.forEach(r => keepInstances.push(InstanceManager.getInstance(r.dataset["instanceid"])));
        }
        this._instances.keep(keepInstances);
    }

    protected _attributeChangedEventMethod = (sender: OWObject, attributeName: string) => {
        // pokud uživatel změnil hodnotu v komponentě, tak se odstraňují existující validační chyby
        this.validationManager.removeDataErrors(sender.id, attributeName);
    };

    /**
     * Resolvne promise čekající na dokončení změny objektu
     * @param value Typ dokončení promise - úspěšně/neúspěšně
     */
    protected override _resolveActualObjectSetDeferred(value: boolean): void {
        super._resolveActualObjectSetDeferred(value);

        if (!this._selectedRecordChangeDeferred.isResolved) {
            this._selectedRecordChangeDeferred.resolve(value);
        }
    }

    /**
     * Při změně vybraného objektu aktualizuje některé info n řádku
     */
    private _updateRowInfo(): any {
        const row = this.currentDataGrid.selectedRows[0];
        if (row)
            (row as HTMLElement).dataset["instanceid"] = this.actualObject.instanceID;
    }

    /**
     * Při změně vybraného objektu aktualizuje hodnoty řádku na hodnoty objektu
     */
    protected async _updateRowData(): Promise<void> {
        if (this.currentDataGrid.selectedRows.length === 1) {
            const row = this.currentDataGrid.selectedRows[0];

            if (row.isDisposed()) { // TODO Provizorní kontrola. Opravit příčinu práce s disposed řádky v rámci tasku 33553.
                return Promise.resolve();
            }

            await Promise.all(
                row.dataCells.map(
                    (async (cell) => {
                        try {
                            if (!this.actualObject)
                                return;
                            const objValue = await this.actualObject.getValue(cell.column.attrName);
                            if (objValue == null || objValue === SerializeFuncs.deserializeUnknown(""))
                                return;
                            if (cell.dataSet && cell.dataSet.length > 0 && cell.dataSet[0] != objValue && !(cell.dataSet[0] instanceof NullOWObject))
                                row.setData(cell.column.attrName, objValue);
                        } catch (e) {
                            if (e instanceof InvalidTypeCasting) {
                                this._logger.warn(e.message);
                            } else throw e;
                        }
                    }),
                ),
            );
        }
    }

    protected async _createAndInitDataGrid(): Promise<void> {
        this.dataGrid = new EditableDataGrid();

        if (this.dataGrid.emptyMessage) {
            this.dataGrid.emptyMessage.emptyMessageCustomization = this.emptyMessageCustomization;
        }

        if (this.fontColorAttr) {
            this.dataGrid.flagAttributeName = this.fontColorAttr;
            this.compactDataGrid.flagAttributeName = this.fontColorAttr;
        }

        if (this.fontColorFlags) {
            const fontColorFlags = this.fontColorFlags.split(",");
            this.dataGrid.allowedFlagColors = fontColorFlags;
            this.compactDataGrid.allowedFlagColors = fontColorFlags;
        }

        this.dataGridCreated.trigger(this, this.dataGrid);

        this.dataGrid.frozenColumnCount = this._frozenColumnCount;
        this.dataGrid.dataSource = this.dataSource;
        this.dataGrid.orderBy = this.orderBy;
        this.dataGrid.objectName = this.objectName;
        this.compactDataGrid.objectName = this.objectName;
        this.dataGrid.isMultiEditAllowed = this.editStyle == EditStyleType.esMulti;
        this.dataGrid.setExtendedProperties(this._getExtendedProperties());

        this._events.addListener(this.dataGrid, "AfterSaved", () => {
            this.autoRefresher?.unpause();
        });

        this._events.addListener(this.dataGrid, "SelectionChanged", (e: IDataGridSelectionChangedEvent) => {
            this.onSelectionChanged(e);
        });
        this._events.addListener(this.dataGrid, "KeyboardSelectionChangeInitiated", (_e: null) => {
            this.keyboardSelectionChangeInitiated.trigger(this, this.dataGrid);
        });

        if (this.disableSort)
            this.dataGrid.disableSort();
        else
            this._events.addListener(this.dataGrid, "OrderingChanged", (e: IColumnOrderingChangedEvent) => {
                this.orderBy = e.detail.order;
                this.profileInfo.order = e.detail.order;
                this.frameProfileChanged.trigger(this, null);
            });

        this._events.addListener(this.dataGrid, "ColumnPositionChanged", (e: IColumnPositionChangedEvent) => {
            // newIndex z události počítá pouze s viditelnými slopuci, ale v profilu potřebujeme hodnotu vztaženou ke všem
            const newOrder = this.currentDataGrid.columns.findIndex(column => column.attrName === e.detail.column.attrName) + 1;

            this._changeProfileColumnOrder(e.detail.column.attrName, newOrder, e.detail.oldPosition);
        });

        this._events.addListener(this.dataGrid, "DataShown", async () => {
            this._dataShown();
        });

        this._events.addListener(this.dataGrid, "DataLoadingEnded", async () => {
            this._dataLoaded();
        });

        this._events.addListener(this.compactDataGrid, "DataLoadingEnded", async () => {
            this._dataLoaded();
        });

        // Preview událost umožňuje zrušit cílovou událost (ValueChanged buňky), ale neměla by sloužit k ničemu jinému
        this._events.addListener(this.dataGrid, "CellValueChangedPreview", (e: ICellValueChangedPrewAwaitableEvent) => {
            // k možným existujícím taskům z jiných handlerů přidám další - výsledkem celého chainu může být zrušení cílové události (při false)
            e.enqueueResult(async (res) => {
                // pokud už někde dříve bylo výsledkem false, tak nemá cenu provádět další vyhodnocení
                if (res === false)
                    return false;

                await this.waitForSelectedRecordChange();
                const mainPasswordObject = await FrameSharedMethods.findMainObject(this.actualObject); //Vratí buď actual object nebo object nalezený přes passwordMasterLink, pokud ho má.
                if (!mainPasswordObject.hasEncryptionPassword && await this.actualObject.isAttributeEncrypted(e.detail.attrName))
                    return await FrameSharedMethods.showEncryptDecryptDialog(this.actualObject, true);

                return true;
            });
        });
        this._setCurrentDataGrid(this.dataGrid);

        this._events.add(this.dataGrid.dataSource.loadingStarted, () => {
            UIManager.instance.showLoader();

            if (this._dataLoadingDefferred == null) {
                this._dataLoadingDefferred = PromiseUtils.defer();
            }
            if (this._dataLoadingEndedDefferred == null) {
                this._dataLoadingEndedDefferred = PromiseUtils.defer();
            }

            this._dataLoadingEndedDefferred.promise.finally(() => UIManager.instance.hideLoader());
        });

        this._events.add(this.dataGrid.dataSource.dataLoaded, () => {
            this.dataLoaded.trigger(this, null);
        });

        this._events.add(this.dataGrid.dataSource.loadingEnded, (_sender, eventData: IDataSourceLoadInfo) => {
            if (eventData.emptyData) { // emptyData znamená, že dataLoaded neproběhl a je třeba dokončit promise
                this._resolveDataLoadingDeffer(true);
            }
            this._resolveDataLoadingEndedDeffer(true);
        });

        this._events.add(this.dataGrid.dataSource.error, () => {
            this._dataLoadingDefferred?.reject();
            this._dataLoadingDefferred = null;
            this._dataLoadingEndedDefferred?.reject();
            this._dataLoadingEndedDefferred = null;

            this.dataLoadingFailed.trigger(this, null);
        });

        this._events.add(this.dataGrid.dataSource.loadingCancelled, () => {
            this._resolveDataLoadingDeffer(false);
            this._dataLoadingEndedDefferred?.reject(new CancelError("LoadingEnd"));
            this._dataLoadingEndedDefferred = null;

            this.dataLoadingCancelled.trigger(this, null);
        });

        if (this.masterFrame) {
            this._events.add(this.masterFrame.actualObjectChanged, async (_master: IMasterableFrame, eventArgs: IActualObjectChangedEventArgs) => {
                await this._requestDynamicColumnsUpdate();

                this._onMasterObjectChanged(eventArgs);
            });
        }

        if (this.dataGrid instanceof IEditableListDataGrid && await this._getClassEncryptingAllowed()) {
            this.dataGrid.canSwitchToEditCallback = (): boolean => {
                return true;
            };
        }

        await this._initColumnsAndLoadData();
    }

    /** Je spuštěna po načtení dat */
    protected async _dataShown(): Promise<void> {
        this._resolveDataLoadingDeffer(true);
    }

    /** Je spuštěna po dokončení načítání dat */
    protected async _dataLoaded(): Promise<void> {
        this.autoRefresher?.resetRefreshTimeout();
        this._resolveDataLoadingEndedDeffer(true);
    }

    /**
     * Dokončí promise čekající na načtení dat
     * @param success Zda má být dokončen úspěšně či neúspěšně
     */
    private _resolveDataLoadingDeffer(success: boolean): void {
        if (this._dataLoadingDefferred) {
            this._dataLoadingDefferred.resolve(success);
            this._dataLoadingDefferred = null;
        }
    }

    /**
     * Dokončí promise čekající na ukončení načítání dat
     * @param success Zda má být dokončen úspěšně či neúspěšně
     */
    private _resolveDataLoadingEndedDeffer(success: boolean): void {
        if (this._dataLoadingEndedDefferred) {
            this._dataLoadingEndedDefferred.resolve(success);
            this._dataLoadingEndedDefferred = null;
        }
    }

    /**
     * Ověří a případně počká na stav, kdy je možné spustit aktualizaci dynamických sloupců a pak ji provede
     */
    protected async _requestDynamicColumnsUpdate(): Promise<void> {
        if (this._addAttrsListMethod == null || !this._columnLoadingFinished.isSettled) { // Už se na vyhodnocení čeká v rámci předchozí změny mastera
            return;
        }

        this._columnLoadingFinished = PromiseUtils.deferStateful<boolean>(); // Potřebujeme počkat s načítáním dat až se dokončí aktualizace sloupců

        try {
            if (this.currentDataGrid.canValueLoad) {
                await this._updateDynamicColumns();

                this._columnLoadingFinished.resolve();
            } else {
                const canValueLoadKey = "genericListrframeCanValueLoad";

                this._events.addListenerWithKey(canValueLoadKey, this.currentDataGrid, "canvalueload", async (e: IBooleanDetailValueEvent) => {
                    if (e.detail.value) {
                        await this._updateDynamicColumns();

                        this._events.removeByKey(canValueLoadKey); // Nechceme, aby se spouštěl znovu

                        this._columnLoadingFinished.resolve();
                    }
                });
            }
        } catch (err) {
            ErrorHandler.instance.handleError(err);

            this._columnLoadingFinished.resolve();
        }
    }

    /**
     * Metoda volana pri zmene master zaznamu
     */
    protected override async _onMasterObjectChanged(_masterObjectChangedArgs?: IActualObjectChangedEventArgs): Promise<void> {
        if (this._actualObjectCTS) { // zruší případné dočítání předchozího záznamu
            this._actualObjectCTS.cancel();
            this._actualObjectCTS = undefined;
        }

        this._actualObjectCTS = new CancellationTokenSource();

        await this._waitForColumnLoad(); // Čeká kvůli načítání dat, dokud nejsou aktualizovány případné dynamické sloupce
        if (!this._isInitialized)
            await this.initialized.waitForEventTrigger(30000); // před načtením dat je potřeba počkat na doběhnutí inicializace framu
    }

    /**
     * Zajistí, že se načítání dat spustí až po dokončení vstupního promise
     * @param defer - po jeho dokončení nebo zrušení je teprve započato načítání dat
     */
    public deferDataLoad(defer: Deferred<boolean>): void {
        this._dataLoadingDefferred = defer;
    }

    protected async _getClassDescription(): Promise<IOWClassDescription> {
        if (this.__classDescription === null)
            this.__classDescription = await ModelDescriptionsManager.instance.getClassInfo(this.objectName);
        return this.__classDescription;
    }

    /**
     * Získá z descriptiony třídy, zda je třída šifrovatelná
     */
    protected async _getClassEncryptingAllowed(): Promise<boolean> {
        return (await this._getClassDescription()).encryptingAllowed;
    }

    /**
     * Získá z descriptiony třídy seznam jmen atributů s povoleným šifrováním
     */
    protected async _getAttributeNamesWithEncryptingAllowed(): Promise<ReadonlyArray<string>> {
        const classDescription = await this._getClassDescription();
        return classDescription.attributes
            .filter(({ encryptingAllowed }) => encryptingAllowed)
            .map(({ attributeName }) => attributeName);
    }

    /**
     * Vytvoří sloupce seznamu a spustí načtení dat
     */
    protected async _initColumnsAndLoadData(): Promise<void> {
        try {
            if (this.currentDataGrid.canValueLoad) {
                await this._createColumns(FrameSharedMethods.getColumnsDefinition(this.element));
                await this._updateDynamicColumns();

                this._createDesktopDropDownMenu();
            } else {
                this.currentDataGrid.registerEventListener("canvalueload", async (e: IBooleanDetailValueEvent) => {
                    if (!this.currentDataGrid.initialized && e.detail.value) {
                        await this._createColumns(FrameSharedMethods.getColumnsDefinition(this.element));
                        await this._updateDynamicColumns();

                        this._createDesktopDropDownMenu();
                    }
                });
            }
        } catch (err) {
            ErrorHandler.instance.handleError(err);
        }
        this._columnLoadingFinished.resolve();
    }

    /** Zobrazení dialogu pro ověření, zda chce uživatel skutečně smazat označené záznamy */
    protected _confirmDeleteDialog(): Promise<MessageButtonResult> {
        return MessageWindow.showYesNoDialog(new LocalizedStringBuilder("GUI-218490"), "GUI-5", "GUI-33412"); //"GUI-218490"=Skutečně chcete smazat označené záznamy?, "GUI-5"=Smazat,"GUI-33412"=Zrušit
    }

    public override async validate(_checkRecommended?: boolean, _checkPostponedRequired?: boolean, _validatedUnchanged?: boolean): Promise<boolean> {
        await this.waitForPendingActions();
        await this._checkDataGridEmptyRequiredComponents();

        return !this.validationManager.hasErrors;
    }

    /**
     * Umožňuje čekat na běžící procesy na komponentě
     */
    public async waitForPendingActions(): Promise<void> {
        const pendingTasks = new Array<Promise<void>>();
        const awaitableCells = this._getAllEditedRowsCells();
        for (const cell of awaitableCells) {
            pendingTasks.push(cell.waitForPendingActions());
            await cell.waitOnDataLoaded();
        }

        await Promise.all(pendingTasks);
    }

    /**
     * Umožňuje zrušit běžící procesy na komponentě
     */
    public cancelPendingActions(): void {
        const awaitableCells = this._getAllEditedRowsCells();
        for (const cell of awaitableCells) {
            cell.cancelPendingActions();
        }
    }

    public override async waitForSelectedRecordChange(): Promise<boolean> {
        await super.waitForSelectedRecordChange();

        return this._selectedRecordChangeDeferred.promise;
    }

    protected _cancelPendingTaskOnRow(key: string): void {
        const awaitableCells = this._getByKeyEditedRowsCells(key);
        for (const cell of awaitableCells) {
            cell.cancelPendingActions();
        }
    }

    private _getAllEditedRowsCells(): TableCell[] {
        const awaitableCells = new Array<TableCell>();
        if (this.dataGrid instanceof IEditableListDataGrid && this.dataGrid.isInEdit) {
            for (const row of this.dataGrid.editedRows) {
                for (const cell of row.dataCells) {
                    awaitableCells.push(cell);
                }
            }
            if (awaitableCells.length == 0 && this.dataGrid.selectedRows.length > 0 && this.dataGrid.selectedRows[0].isInEdit)
                for (const cell of this.dataGrid.selectedRows[0].dataCells) {
                    awaitableCells.push(cell);
                }
        }
        return awaitableCells;
    }

    private _getByKeyEditedRowsCells(key: string): IBussyAwaitable[] {
        const awaitableCells = new Array<IBussyAwaitable>();
        if (this.dataGrid instanceof IEditableListDataGrid && this.dataGrid.isInEdit) {
            const row = this.dataGrid.editedRows.find((item) => item.dataKey == key);
            if (row) {
                for (const cell of row.dataCells) {
                    awaitableCells.push(cell);
                }
            }
        }
        return awaitableCells;
    }

    private async _checkDataGridEmptyRequiredComponents(notSelected: boolean = false, ct?: CancellationToken): Promise<void> {
        if (!(this.dataGrid instanceof IEditableListDataGrid) || !this.dataGrid.isInEdit)
            return;

        const editableDataGrid = this.dataGrid;
        for (const row of editableDataGrid.editedRows) {
            if (row.isSelected && notSelected)
                continue;

            const rowPermData = new RowPermissions();
            const eds = editableDataGrid.dataSource;
            const rowData = await eds.getValues(row.dataKey);
            if (!rowData) // Objekt s daným dataKey v dataSourcu neexistuje
                continue;
            if (!row.isInEdit && !row.isChanged)
                continue; // behem nacitani dat preslo do stavu needitace, takze se nebude pokracovat ve vyhodnocovani

            if (eds.permissionKey != undefined && rowData[eds.permissionKey]) {
                const tempPerm = rowData[eds.permissionKey].valueOf() as Array<IPermissionData>;
                for (const p of tempPerm) {
                    rowPermData.set(p.attrName, p);
                }
            }

            for (const cell of row.dataCells) {
                if (!cell.column.visible || !cell.column.isEditable)
                    continue;

                const cellPerm = rowPermData.get(cell.column.attrName);
                const rowCellData = rowData[cell.column.attrName];
                if (!cellPerm || !cellPerm.enabled || !cellPerm.required || (rowCellData != null && rowCellData.valueOf() != null && rowCellData.valueOf() !== "" && !(rowCellData.valueOf() instanceof NullOWObject)))
                    continue;

                const obj = await this._getObject(row.dataKey, undefined, ct);
                const errMsg = await Localization.getTextPromise("GUI-55627"); // "Nevalidní nebo nevyplněné povinné pole"

                cell.setInvalid(null, errMsg);
                this.validationManager.addComponentError(obj as any as IValidationDataObject<IValidationData>, {
                    type: ValidationErrorType.requiredError,
                    errorMessage: errMsg,
                    componentNames: [cell.column.columnName],
                }, {
                    control: cell,
                    attributeName: cell.column.attrName,
                    container: row,
                    showsInvalidData: () => true,
                });
            }
        }
    }

    public getAttributesOrdered(): HTMLElement[] {
        const attrs = Array.from(this.element.querySelectorAll("[data-behaviour='column']")) as HTMLElement[];
        const orderAttrs: HTMLElement[] = [];

        for (const col of this.dataGrid.columns) {
            orderAttrs.push(attrs.find((item) => StringUtils.compareCaseInsensitive(item.getAttribute("data-attrname"), col.attrName)));
        }

        return orderAttrs;
    }

    protected _createDesktopDropDownMenu(): void {
        this._headerDropDown = new DropDownMenu();

        if (!this._hideFilterRow)
            this._headerDropDownFilterItem = FrameSharedMethods.createColumnDropDownMenuFiltering(this._headerDropDown, this.frameContext);

        if (!this.disableSort)
            FrameSharedMethods.createColumnDropDownMenuOrdering(this._headerDropDown, this.dataGrid, this.objectName, () => this._headerDropDownHc, (newOrdering: IOrderBy[]) => {
                this._orderByInitiated.trigger(this, newOrdering);
                this.profileInfo.order = newOrdering;
            });

        // TODO: odpoznámkovat a přesunout na požadované místo, jak bude doplněna funkcionalita - volby k agregačním funkcím
        // this.headerAddItem(DropDownMenuWithSubmenuItem.createDropSubMenuItem("Functions", "GUI-309898", submenu)); // agregacni funkce

        this._headerDropDown.addItem(new SeparatorMenuItem());

        const hideColumnItem = DropDownMenuItem.createDropMenuItem("ui-eye", async () => {
            if (await this.ownerForm.checkAndSaveForm({ forceSave: this._suppressSaveDialog })) {
                this._headerDropDownHc.visible = false;
                this._setProfileColumnInfoAttr(this._headerDropDownHc.attrName, FrameColumnInfoAttributes.visible, false);
                this.frameProfileChanged.trigger(this, null);
            }
        }, "GUI-584566", true);
        hideColumnItem.classList.add("HideColumnItem");
        this._headerDropDown.addItem(hideColumnItem); // skrýt sloupec

        // TODO: odpoznámkovat a přesunout na požadované místo, jak bude doplněna funkcionalita - volby k agregačním funkcím
        /*submenu.AddItem(DropDownMenuItem.createDropMenuItem("none", () => { }, "GUI-28108")); // suma
        submenu.AddItem(DropDownMenuItem.createDropMenuItem("none", () => { }, "GUI-584567")); // prumer
        submenu.AddItem(DropDownMenuItem.createDropMenuItem("none", () => { }, "GUI-584568")); // pocet
        submenu.AddItem(DropDownMenuItem.createDropMenuItem("none", () => { }, "GUI-310048")); // maximum
        submenu.AddItem(DropDownMenuItem.createDropMenuItem("none", () => { }, "GUI-310046")); // minimu*/
    }

    protected _setProfileColumnInfoAttr(attrName: string, propertyName: FrameColumnInfoAttributes, value: any, updateColumnPanel: boolean = true): void {
        let val = this.profileInfo.columns[attrName];
        if (propertyName === FrameColumnInfoAttributes.width && value < Column.MIN_COLUMN_WIDTH)
            value = Column.MIN_COLUMN_WIDTH;
        if (val == null) {
            val = {
                attrName: attrName,
            };
            val[FrameColumnInfoAttributes[propertyName]] = value;
            this.profileInfo.columns[attrName] = val;
        } else {
            val[FrameColumnInfoAttributes[propertyName]] = value;
        }

        if (updateColumnPanel && propertyName !== FrameColumnInfoAttributes.width) {
            this._triggerColumnsUpdated();
        }
    }

    private async _triggerColumnsUpdated(): Promise<void> {
        this.columnsUpdated.trigger(this, {
            columns: this.dataGrid.columns,
            profileInfo: this.profileInfo,
        });
    }

    /**
     * Upraví pozici sloupce v profilu včetně vyvolání události informující o změně profilu
     * @param attrName Přemístěný attribut
     * @param newOrder Nová pozice
     * @param oldOrder Původní pozice
     */
    protected _changeProfileColumnOrder(attrName: string, newOrder: number, oldOrder?: number, updateColumnPanel: boolean = true): void {
        this._setProfileColumnInfoAttr(attrName, FrameColumnInfoAttributes.order, newOrder, updateColumnPanel);

        this._updateProfileColumnsOrder(newOrder, attrName, oldOrder, (oldOrder >= newOrder));

        this.frameProfileChanged.trigger(this, null);
    }

    /**
     * Provede aktualizaci pořadí všech sloupců v profilu
     *
     * @param orderValue pořadí ze kterého se výchazí tedy zvětšuji se položky, které jsou stejné nebo si rovny
     * @param skipAttrName atribut, ktery se preskoci a netyka se ho uprava poradi
     * @param originOrderValue původni číslo řazení, pokud je vyplnění různě od -1, provádí se uprava do tohoto cisla, dal se již nezvešují a to proto, že pokud mění pořadi třeba ze 5 na 3, je treba ovlivni element mezi 3. az 5. pozici, ale nemeli by se upravovat elementy dal
     * @param isAdd Zda má dojít k přičtení 1 k pořadí sloupců (posun směrem doprava). Jinak se odečítá 1 (posun doleva)
     */
    protected _updateProfileColumnsOrder(orderValue: number, skipAttrName: string, originOrderValue: number = -1, isAdd: boolean = true): void {
        const keys = Object.keys(this.profileInfo.columns);

        const min = Math.min(orderValue, originOrderValue);
        const max = Math.max(orderValue, originOrderValue);

        const checkInRangeFnc = (ord: number) => {
            if (originOrderValue == -1) {
                return ord >= orderValue;
            }
            if (ord >= min && ord <= max)
                return true;

            return false;
        };

        for (const key of keys) {
            if (key == skipAttrName)
                continue;

            const col = this.profileInfo.columns[key];

            if (col.order && checkInRangeFnc(col.order)) {
                if (col.frozen)
                    continue;

                if (isAdd)
                    col.order++;
                else
                    col.order--;
            }
        }
    }

    /** Provede refresh po změně profilu */
    protected async _profileChangeRefresh(hasNewlyAddedAttributes: boolean): Promise<void> {
        if (this.masterFrame != null && this.masterObject != null || this.masterFrame == null) {
            // pokud již byla načtena data po otevření formuláře (první načtení obstará inicializace; nastavení výchozího profilu při otevření formuláře by tady způsobovalo znovunačtení)
            // a byla změněna podmínka nebo přibyl nějaký atribut, který by se měl načítat
            if (this._firstLoadFinished && (this._conditionUpdated || hasNewlyAddedAttributes)) {
                await this.dataSource.loadNewData(undefined, this._cancelPrevAndCreateDataLoadingCTS().token);
                this._conditionUpdated = false;

                return;
            }

            await this.waitForDataLoad();
            await this.dataGrid.reRenderData();
        }
    }

    /**
     * Odstraní dynamické sloupce ze seznamu
     */
    protected _removeDynamicColumns(): void {
        this.dataGrid.removeDynamicColumns();

        const attributes = this.dataSource.getAttrs();
        let fullLenghtAttributes = this.dataSource.getFullLengthAttrs();
        this._dynamicColumns.forEach((columnData) => { // Zbavíme se atributů a třídění odebíraných sloupců
            attributes.splice(attributes.indexOf(columnData.attrName), 1);

            const orderByIndex = this.orderBy.findIndex(column => column.attributeName === columnData.attrName);

            if (orderByIndex >= 0) {
                this.orderBy.splice(orderByIndex, 1);
            }
        });
        fullLenghtAttributes = fullLenghtAttributes.filter(attrName => attributes.includes(attrName));
        this.dataSource.setAttrs(attributes, fullLenghtAttributes);
    }

    public async lockObject(object: OWObject = this.actualObject): Promise<boolean> {
        if (this.onEditMethod && object) {
            try {
                await object.run({
                    methodName: this.onEditMethod,
                    requestContext: this._getRequestContext(),
                });
            } catch (err) {
                ErrorHandler.instance.handleError(err);
                return false;
            }
        }
        return true;
    }

    public async unlockObject(object: OWObject = this.actualObject): Promise<boolean> {
        if (object && this.onCancelMethod && !object.isNew)
            try {
                await object.run({
                    methodName: this.onCancelMethod,
                    requestContext: this._getRequestContext(),
                });
            } catch (err) {
                ErrorHandler.instance.handleError(err);
                return false;
            }
        return true;
    }

    /**
     * Aktualizuje dynamické sloupce podle výstupu z aplikační metody
     */
    protected async _updateDynamicColumns(): Promise<void> {
        if (!this._addAttrsListMethod) {
            return;
        }

        const obj = this._dynamicColumnsObject;
        if (!obj || obj instanceof MEOWObject) {
            return;
        }

        try {
            UIManager.instance.showLoader();

            const columns = await OWManager.getListFrameColumns(this._addAttrsListMethod, obj?.instanceID, this._getRequestContext());

            if (this._dynamicColumns) { // Ověříme, zda je vůbec potřeba sloupce přegenerovat a nebo zda stačí ponechat již vytvořené
                if (DataInterfaceComparer.arraysEquals(this._dynamicColumns, columns)) {
                    return;
                }
                this._removeDynamicColumns();
            }
            this._dynamicColumns = columns; // Pro případné příští porovnání a odstranění

            const columnsDefinitions: IColumnDefinition[] = columns.map(columnData => {
                const column: IColumnDefinition = {
                    dataType: "attribute",
                    attrName: columnData.attrName,
                    locKey: columnData.locKey,
                    locParams: columnData.localizationParams,
                    displayFormat: columnData.displayFormat,
                    orderBy: columnData.orderBy,
                    width: columnData.width,
                    isEditable: columnData.isEditable,
                };

                if (columnData.orderBy) {
                    this.orderBy.push({ order: columnData.orderBy === "desc" ? OrderType.descending : OrderType.ascending, attributeName: columnData.attrName });
                }

                return column;
            });

            if (columnsDefinitions.length) {
                await this._createColumns(columnsDefinitions, true);
            }

            await this._triggerColumnsUpdated();
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /**
     * Vytvori seznam sloupcu
     * @param columnsDefinition - definice sloupců, které mají být přidány do seznamu
     * @param isDynamic - dynamicky přidávané sloupce = nedojde k přepsání sloupců z definice, ale jen k přidání nových sloupců
     */
    protected override async _createColumns(columnDefinitions: IColumnDefinition[], isDynamic: boolean = false): Promise<void> {
        const columns = new Array<Column>();
        const compactColumns = new Array<Column>();
        let attrs = new Array<string>();
        let useCompactInfo = false;
        const compactInfo: ICompactDataGridAttrInfo = {
            topLeft: [],
            topRight: [],
            bottom: [],
        };
        const topLeftMap: Map<string, number> = new Map<string, number>();
        const topRightMap: Map<string, number> = new Map<string, number>();
        const bottomMap: Map<string, number> = new Map<string, number>();

        const columnInitParams: IColumnParameters = { readVersioned: this.readVersioned, defaultWhisperer: AppContext.instance.configuration.defaultWhisperer };
        try {
            const attrMap = await FrameSharedMethods.mapColumnDefinitionsToAttributeInfos(this.objectName, columnDefinitions);

            let i = 0;
            for (const attr of attrMap) {
                const column = await ColumnFactory.initColumn(attr.columnDefinition, attr.attributeInfo.type, attr.attributeInfo, columnInitParams, this.disableSort);
                const compactColumn = await ColumnFactory.initColumn(attr.columnDefinition, attr.attributeInfo.type, attr.attributeInfo, columnInitParams, this.disableSort, true);
                const compactArea = attr.columnDefinition.compactArea;
                const compactOrder = attr.columnDefinition.compactAreaOrder;

                if (isDynamic) {
                    column.isDynamic = true;
                    compactColumn.isDynamic = true;
                }

                if (i++ < this._frozenColumnCount) {
                    column.isFrozen = true;
                }
                // pokud je sloupec editovatelny, tak se jeho hodnota upravuje na zaklade povoleni editovatelnosti pro cely seznam
                if (column.isEditable) {
                    column.isEditable = this.isEditable;
                }

                // zpracovani kompaktniho zobrazeni
                if (compactArea && compactArea.length > 0) {
                    useCompactInfo = true;
                    if (compactArea === "topleft") {
                        topLeftMap.set(column.attrName, compactOrder === -1 ? compactInfo.topLeft.length : compactOrder);
                    } else if (compactArea === "topright") {
                        topRightMap.set(column.attrName, compactOrder === -1 ? compactInfo.topLeft.length : compactOrder);
                    } else if (compactArea === "bottom") {
                        bottomMap.set(column.attrName, compactOrder === -1 ? compactInfo.topLeft.length : compactOrder);
                    }
                }

                //#region Prace s LOVColumn
                if (column instanceof IRequiresMasterObject) {
                    column.registerEventListener("masterObjectRequest", (e: IMasterObjectRequest): void => {
                        // pokud už je na seznamu načtený potřebný objekt, tak se nasetuje ten
                        if (this.actualObject != null && (this.actualObject.id == e.detail.objectId || this.actualObject.instanceID == e.detail.objectId)) {
                            e.detail.control.actualObject = this.actualObject;
                        } else if (Number.isNaN(+e.detail.objectId) && this.dataSource instanceof IObjectDataSource) {
                            // id záznamu není vůbec číslo, pravděpodobně to je nový záznam - podle jeho "klientskeho id" ho nacteme z datasource
                            this._getObject(e.detail.objectId.toString()).then(obj => e.detail.control.actualObject = obj);
                        } else {
                            // jinak se zaregistruje listener na změnu objektu a v návaznosti na událost se nasetuje objekt
                            const groupName = "masterObjectRequest_wait" + this.staticCounter;

                            // timeout pro odregistrování listeneru - pouze kvůli tomu, aby nezůstávaly viset nepotřebné listenery
                            const cts = new CancellationTokenSource();
                            PromiseUtils.delay(3000, cts.token)
                                .then(() => BaseControl.unregisterEventListener(groupName))
                                .catch(PromiseUtils.cancellationCatch);

                            this.actualObjectChanged.add(() => {
                                if (this.actualObject == null)
                                    return;

                                // pokud uživatel stihnul překliknout na jiný záznam
                                if (this.actualObject.id !== e.detail.objectId && this.actualObject.instanceID !== e.detail.objectId) {
                                    return;
                                }

                                cts.cancel();
                                e.detail.control.actualObject = this.actualObject;
                                BaseControl.unregisterEventListener(groupName);
                            });
                        }

                        if (e.detail.control instanceof LOVEdit) {
                            if (e.detail.data instanceof IOWObject && e.detail.control.lovEdit.className == e.detail.data.objectName)
                                e.detail.control.value = e.detail.data; //obcas nechodil OWObject
                        }
                    });
                }

                // TODO: predelat na rozhrani
                if (column instanceof FileColumn) {
                    const eventListener = async (e: CustomEvent) => {
                        if (!e.detail.rowId)
                            return;

                        let instanceId: string;
                        if (e.detail.rowId.startsWith("iid")) {
                            instanceId = e.detail.rowId;
                        } else if (this.dataSource instanceof EditableDBDataSource) {
                            const obj = await this._getObject(e.detail.rowId);
                            instanceId = obj.instanceID;
                        }

                        if (instanceId)
                            DownloadManager.downloadFile(instanceId, e.detail.attrName, "", () => {
                                // do nothing.
                            });
                    };
                    column.addEventListener("downloadRequest", eventListener);
                    compactColumn.addEventListener("downloadRequest", eventListener);
                }

                if (column instanceof LovColumn) {
                    const eventListener = async (e: CustomEvent) => {
                        if (!e.detail.id)
                            return;

                        let obj: OWObject;
                        if (e.detail.id.startsWith("iid")) {
                            obj = await OWManager.getInstance(e.detail.id);
                        } else if (this.dataSource instanceof DBDataSource) {
                            obj = await this._getObject(e.detail.id);
                        }

                        if (obj) {
                            LOVEditMaster.openDetailForm({
                                object: obj,
                                attrName: column.attrName,
                                formName: column.editWindow,
                                parametricData: {
                                    condition: `ID = ${await obj.getObjectAttributeID(column.attrName)}`,
                                    ownerForm: this.ownerForm,
                                    inLOVMode: true,
                                    applyLOVCondition: true,
                                    readVersioned: this.readVersioned,
                                    formContext: this.frameContext,
                                    visibleOnlyDetail: column.showOnlyDetail,
                                    transactionContextGetter: this.frameContext?.getTransactionContextId,
                                },
                            }, ModalWindowType.lov);
                        }
                    };
                    column.addEventListener("openDetailForm", eventListener);
                    compactColumn.addEventListener("openDetailForm", eventListener);
                }

                //#endregion Prace s LOVColumn

                this.registerHeaderClickEvent(column);
                this.registerHeaderClickEvent(compactColumn);

                column.registerEventListener("dropdownmenushow", (e: CustomEvent) => this._dropdownMenuShow(e));

                column.registerEventListener("visibility", async (e: CustomEvent) => {
                    if (e.detail.value) { // Pokud zviditelňuji sloupec a některý z framů na formu je změněný, zobrazí se YNC
                        if (!(await this.ownerForm.checkAndSaveForm({ forceSave: this._suppressSaveDialog })))
                            return;
                    }

                    if (!column.headerCell)
                        return;

                    column.headerCell.visibility = e.detail.value;

                    const cols = [...this.dataGrid.columns]
                        .sort((item1, item2) => (item1.order ?? Number.MAX_VALUE) - (item2.order ?? Number.MAX_VALUE));

                    const attrsNew: string[] = ArrayUtils.flat(cols
                        .filter(col => col.visible)
                        .map(col => col.dataSourceAttrNames));
                    if (this.fontColorAttr && this.fontColorAttr.length > 0 && !attrsNew.includes(this.fontColorAttr)) {
                        attrsNew.push(this.fontColorAttr);
                    }

                    this.dataGrid.columns = cols;
                    const fullLengthAttributes = cols.filter(column => column.trimValue === false).map(column => column.attrName);
                    this.dataSource.setAttrs(attrsNew, fullLengthAttributes);

                    if (!e.detail.value) {
                        this.dataGrid.dataRows.forEach(row => row.toggleVisibilityCellByAttrName(column.attrName, e.detail.value));
                    } else {
                        this._columnVisibilityDataLoad();
                    }

                    this.dataGrid.generateColumnsOrder();
                });

                columns.push(column);
                compactColumns.push(compactColumn);

                if (column.visible) {
                    attrs = attrs.concat(column.dataSourceAttrNames);
                }

                this.dataGrid.appendChild(column);
                this.compactDataGrid.appendChild(compactColumn);
            }

            if (this.fontColorAttr && this.fontColorAttr.length > 0) {
                attrs.push(this.fontColorAttr);
            }

            if (useCompactInfo) {
                new Map([...topLeftMap.entries()].sort((a, b) => {
                    return a[1] - b[1];
                })).forEach((_value, key) => {
                    compactInfo.topLeft.push(key);
                });
                new Map([...topRightMap.entries()].sort((a, b) => {
                    return a[1] - b[1];
                })).forEach((_value, key) => {
                    compactInfo.topRight.push(key);
                });
                new Map([...bottomMap.entries()].sort((a, b) => {
                    return a[1] - b[1];
                })).forEach((_value, key) => {
                    compactInfo.bottom.push(key);
                });

                this.compactDataGrid.compactAttributes = compactInfo;
            }

            if (isDynamic) {
                for (let i = 0; i < columns.length; i++) {
                    this.dataGrid.columns.push(columns[i]);
                    this.compactDataGrid.columns.push(compactColumns[i]);
                }
            } else {
                this.dataGrid.columns = columns;
                this.compactDataGrid.columns = compactColumns;
            }

            // Nastavení dotazovaných atributů na základě viditelných sloupců otevíraného nebo defaultního profilu.
            if (!this._addAttrsListMethod && this.ownerForm instanceof Form) {
                const attrVisibilitiesFromProfile = this.ownerForm.profileManager.getColumnsVisibilitiesByFrameName(this.frameName, this.ownerForm.initialFormProfileId);
                if (attrVisibilitiesFromProfile) {
                    const visibleAttrsFromProfile = attrVisibilitiesFromProfile.visibleColumns;
                    const invisibleAttrsFromProfile = attrVisibilitiesFromProfile.invisibleColumns;
                    const attrsToAdd = visibleAttrsFromProfile.filter(attr => attrs.indexOf(attr) === -1); // vyberu viditelné, které ještě v kolekci nemám
                    attrs = attrs.concat(attrsToAdd); // přidám nové viditelné
                    attrs = attrs.filter(attr => invisibleAttrsFromProfile.indexOf(attr) === -1); // odstraním atributy, které jsou v neviditelných
                }
            }

            const fullLengthAttributes = columns.filter(column => column.trimValue === false).map(column => column.attrName);

            if (isDynamic) { // Přidáme dodatečné atributy do dataSource
                const attributes = this.dataSource.getAttrs();

                attrs.forEach((attribute) => {
                    attributes.push(attribute);
                });

                this.dataSource.setAttrs(attributes, fullLengthAttributes);
            } else {
                this.dataSource.setAttrs(attrs, fullLengthAttributes);
            }

            if (isDynamic) { // Je třeba vykreslit dodatečné sloupce
                if (!this.compactDataGrid.isShowed) {
                    this.dataGrid.renderColumns(columns, this.orderBy, this.dataGrid.columns.length - columns.length, false, true);
                }
            }
        } catch (err) {
            this._logger.error(`Unable to load column attribute info for ${JSON.stringify(columnDefinitions.map(column => column.attrName))}`); //`
            throw err;
        }
    }

    protected async _getObject(objectId: string, forceLoad?: boolean, ct?: CancellationToken): Promise<OWObject | null> {
        return await this.dataSource.getObject(objectId, await FrameSharedMethods.createAfterCreateExtendedOperations(this._createExtendedOperationsParams(), false), forceLoad, ct);
    }

    /** Spustí načtení dat na základě zviditelnění sloupce */
    protected _columnVisibilityDataLoad(): void {
        if (this.masterFrame != null && this.masterObject != null || this.masterFrame == null) {
            this.dataSource.loadNewData(undefined, this._cancelPrevAndCreateDataLoadingCTS().token);
        }
    }

    public registerHeaderClickEvent(col: Column): void {
        col.registerEventListener("HeaderClickEvent", async (e: CustomEvent) => {
            const objectClass = await this._getClassDescription();

            const attrType = (await objectClass.getAttributeInfo(col.attrName)).basicType;

            this.dataGrid.changeOrdering(col, e.detail.state === 1 ? OrderType.ascending : e.detail.state === 2 ? OrderType.descending : null, e.detail.shiftKey,
                attrType === BasicDataType.String || attrType === BasicDataType.StrNum ? AdditionalOrdering.linguistic : null);
        });
    }

    public async onSelectionChanged(evt: IDataGridSelectionChangedEvent): Promise<void> {
        if (!this.isMultiSelecting && this._lastSelectedRecordId === evt.detail.rowID)
            return;

        if (this._actualObjectCTS) {
            this._actualObjectCTS.cancel();
        }

        this._lastSelectedRecordId = evt.detail.rowID;
        this._actualObjectCTS = new CancellationTokenSource();

        // Umožní čekat na změnu objektu okamžitě od chvíle kliknutí na záznam - zabraňuje předčasnému otevření menu s položkami předchozího záznamu
        this._tryRenewSelectedRecordChangeDeferred();
    }

    /**
     * Vygeneruje nove podminky a parametry pro cteni dat datasourcem
     * @param ds datasource, kteremu se maji nastavit nove podminky
     */
    public override async updateCondition(ds: IObjectDataSource): Promise<void> {
        this._conditionUpdated = true;

        await super.updateCondition(ds);
    }

    /**
     * Vygeneruje nove podminky a parametry pro cteni dat datasourcem a zahaji nacitani dat
     */
    public updateConditionAndRead(): Promise<void> {
        return this._updateConditionAndRead(this.dataSource);
    }

    /**
     * Vygeneruje nove podminky a parametry pro cteni data datasourcem a zahaji nacitani dat
     * @param ds datasource, kteremu se maji nastavit nove podminky
     * @param readNewData priznak ridici zda se maji hned nacist nova data
     */
    protected override async _updateConditionAndRead(ds: IObjectDataSource, readNewData: boolean = true): Promise<void> {
        if (this.masterFrame &&
            (!this.masterFrame.actualObject || // u podrizeneho framu nema vyznam nacitat data pokud v masteru neni vybran zaznam
                (!(this.masterFrame instanceof SeparateDetailFrame) && this.masterFrame.actualObject.isNew))) { // pokud master neni SeparateDetailFrame a objekt je novy je zbytecne nacitat data {
            ds.condition = "1=0"; //přidá se nesplnitelná podmínka, v případě že seznam nemá dočítat data je potřeba aktualizovat i podmínku DS, tak aby třeba dotaz na počet zobrazených záznamů s novou podmínkou
            this._resolveActualObjectSetDeferred(true); // nebude se dočítat, takže beru actualObject za dočtený
            return;
        }

        this._conditionUpdated = true;

        await this._createConditionAndConditionParams(ds);

        if (!ds.conditionParams)
            return;

        if (readNewData) {
            await ds.loadNewData(true, this._cancelPrevAndCreateDataLoadingCTS().token);
            this._conditionUpdated = false;
        }
    }

    protected _cellValidityChanged(control: IValidatable & IAttributeControl, rowObj: OWObject, validity: IValidationResult): void {
        this.enqueuePendingUpdateTask((async () => {
            if (validity.valid) {
                this.validationManager.removeComponentErrors(rowObj.id, control); // pri zruseni zmen na radku muze byt v ListFramu oznaceny jiny radek a tudiz jiny ActualObject
                return;
            }

            const message = validity.invalidMessage ? validity.invalidMessage : await Localization.getTextPromise(validity.invalidLocKey);

            const tc = (control as any as TableCell);

            const objForValidationManager = rowObj as any as IValidationDataObject<IValidationData>;

            this.validationManager.addComponentError(objForValidationManager, {
                type: ValidationErrorType.inputError,
                errorMessage: message,
                componentNames: [tc.column.columnName],
                invalidValue: validity.invalidValue,
            }, {
                control: control,
                attributeName: tc.column.attrName,
                showsInvalidData: () => true, // radek vzdy zobrazuje jen jeden stejny zaznam
            });
        })());
    }

    protected _rowPermissionsChanged(id: any, changes: IPermissionChanges[]): void {
        for (const componentData of changes)
            if (componentData.control && componentData.required) {
                this.validationManager.removeComponentErrors(id, componentData.control, ValidationErrorType.requiredError);
                componentData.control.validate();
            }
    }

    /**
     * Zapne automatické refreshování dat seznamu
     */
    private _activateAutoRefresh(): void {
        ModelDescriptionsManager.instance.getClassInfo(this.objectName).then((classInfo) => {
            const classNames = classInfo.childClasses.concat(classInfo.relatedClasses, this.objectName);

            this.autoRefresher = new AutoRefresher(this, classNames, DIContainer.instance.create(IDataEventsManager), this._autoRefreshInterval);
            this._events.add(this.autoRefresher.onRefresh, () => this._onAutoRefresh());
        });
    }

    /**
     * Callback vyvolávající automatický refresh dat seznamu, pokud se na něm nenachází chyby
     */
    protected async _onAutoRefresh(): Promise<void> {
        if (this.validationManager.hasErrors) {
            return;
        }

        this._logger.info(`[AutoRefresh] Refreshing data in frame ${this.frameName} of form ${this.ownerForm.formName}`); //`

        await this.currentDataGrid.refreshData();

        if (this.currentDataGrid.selectedRows.length === 0)
            this.isEditing = false;

        if (this.actualObject && !this.actualObject.isChanged && this.actualObject.isValid)
            await this._reloadActualObjectOnly();
    }

    /**
     * Pokud probíhá (nebo probíhalo) načítání dat do seznamu, tak se zruší a vygeneruje se nový token source pro nové načítání
     */
    protected _cancelPrevAndCreateDataLoadingCTS(): CancellationTokenSource {
        if (this._dataLoadingCTS) // předchozí load se zruší
            this._dataLoadingCTS.cancel();

        this._dataLoadingCTS = new CancellationTokenSource();

        if (this._isDisposed) // pokud už je komponenta zrušená, tak budou všechny sourcy zrušené
            this._dataLoadingCTS.cancel();

        return this._dataLoadingCTS;
    }

    public loadProfile(filterId: string, queryMode: boolean): void {
        if (this._queryModeFinished || !queryMode) {
            this.filterManager.selectAndApplyFilterById(filterId, true);
        } else {
            if (filterId !== null) {
                this.filterManager.selectFilterById(filterId, true);
            }
        }
    }
    //#endregion Methods

    //#region dispose
    /**
     * Destruktor
     */
    public override dispose(): void {
        if (this._isDisposed)
            return;

        this._dataLoadingCTS?.cancel();
        this.autoRefresher?.dispose();
        this._headerDropDown?.dispose();
        this._headerDropDownFilterItem?.dispose();
        this._decryptItemAction?.dispose();

        this.editModeChanged.removeAll();
        this.dataGridInitialized.dispose();

        this.dataGridCreated.removeAll();
        this.dataSaved.removeAll();
        this._orderByInitiated.removeAll();
        this.actualObjectSet.removeAll();
        this.columnsUpdated.removeAll();

        this._definitionOrderBy = [];
        this._dynamicColumns = [];

        this.dataGrid?.dispose();

        super.dispose();

        // Menu has to be disposed after super, becase it has registered listeners to datarows disposed event.
        if (this.menu) {
            this.menu.dispose();
        }
    }

    protected _dropdownMenuShow(e: CustomEvent): void {
        const column = e.target as Column;
        // atribut pro LOV musi obssahovat i vazbu // nastaveni attributu dropdown menu
        if (this._headerDropDownFilterItem) {
            this._headerDropDownFilterItem.displayFormat = column.displayFormat;
            this._headerDropDownFilterItem.attrName = column.filterAttrName;
            this._headerDropDownFilterItem.dataType = this._filterManager?.getFilterColumn(column.filterAttrName).dataType ?? column.dataType;
        }
        if (column !== this._headerDropDownHc || !this._headerDropDown.visible) {
            if (this._filterManager && this._filterManager.currentFilter == null) {
                const fblock = this._filterManager.createNew(false);
                this._filterManager.selectFilter(fblock);
                this._filterManager.updateFilterPanel();
            } else if (this._filterManager && this._filterManager.filterPanel.currentFilterBlock !== this._filterManager.currentFilter) {
                /* Tato větev je pro případ, kdy uživatel používá filtry pro více frame na jednom formuláři a přechází mezi nimi pomocí kliknutí na hlavičku v DataGridu.
                Bez této větve se nepřekreslí obsah filtr panelu a dojde ke ztrátě vazby FiltrBlocku na frame a tím pádem nedojde k filtrování. */
                this._filterManager.updateFilterPanel();
            }
            // Vybrani zalozky v toolbaru
            if (!this.hideToolBar)
                this.menu.setProperty(FormUICustomProperty.selected, true);

            this._headerDropDownHc = e.target as Column; // Je nastaven ještě přes otevřením, aby šel použít v události "open" vyvolané v show()

            const sortItems = this._headerDropDown.getElementsByClassName("SortMenuItem");
            for (const item of sortItems)
                item.classList.toggle("hidden", !column.canSort);
            this._headerDropDown.getElementsByClassName("NoSortMenuItem")[0]?.classList.toggle("hidden", column.canSort);

            this._headerDropDown.getElementsByClassName("HideColumnItem")[0]?.classList.toggle("hidden", column.order <= this.dataGrid.frozenColumnCount);

            this._headerDropDown.show(e.detail.dropdownelemnt, e, e.detail.originalEvent.detail === 0); // detail === 0 je v eventu nastaven, pokud přijde z klávesnice

            if (this._headerDropDownFilterItem)
                this._headerDropDownFilterItem.focus();
        }
    }

    //#endregion dispose
}

export type OpenModalDetailOption = { openInEdit?: boolean; objID?: string; showOnMainForm?: boolean; openInMultiEdit?: boolean; openMode?: OpenModeType };
